import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent as ReactDragEvent, type PointerEvent as ReactPointerEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronLeft, ChevronRight, Download, ExternalLink, GripVertical, MessageSquarePlus, Paperclip, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { HubBlobUrlPreview } from "@/components/HubBlobUrlPreview";
import { HubWorkspacePage } from "@/components/layout/HubWorkspace";
import { apiFetch, apiFetchBlob, apiUploadFormData } from "@/lib/api";
import { freshdeskTicketUrl } from "@/lib/freshdeskAgentUrl";
import { Button } from "@/components/ui/Button";
import { useHubToast } from "@/components/ui/HubToast";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { isUploadableFile } from "@/components/ui/filePickerAccept";
import {
  dragEventHasFiles,
  SALES_ORDER_ATTACHMENT_ACCEPT,
  salesOrderAttachmentFilesFromDrop,
} from "./salesOrderAttachmentDrop";
import { MentionTextarea } from "@/components/MentionTextarea";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { TextChipInput } from "@/components/ui/TextChipInput";
import { HoverHintTrigger, PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { HoverHintDefinitionGrid } from "@/components/ui/HoverHintDefinitionGrid";
import { cn, formatTimestamp } from "@/lib/utils";
import { useBrowserUnsavedChangesGuard } from "@/lib/useBrowserUnsavedChangesGuard";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import { buildMegaventorySalesOrderUrl } from "@/lib/megaventory";
import { buildInsideSalesApprovalPath, buildInsideSalesSalesOrderEditPath } from "@/lib/hubEntityDetailPaths";
import { rerunInsideSalesSalesOrderRepSync } from "@/lib/insideSalesSalesOrders";
import { checkSalesOrderZipCities } from "./salesOrderZipCityCheck";
import { useIntegratorMe, isIntegratorAdmin } from "@/lib/me-query";
import { salesOrderStatusLabel } from "@/lib/salesOrderHubStatus";
import { insideSalesUserMayDownloadSalesOrderPdf } from "./salesOrderPdfDownloadAccess";
import { useHubCacheStream } from "@/lib/useHubCacheStream";
import { useHubModuleAdminSettings, useHubModuleWrite } from "@/lib/useHubModuleWrite";
import {
  applyAutomaticChargeLines,
  formatMagnetCatalogDescription,
  isRmaReplacementCustomerPurchaseOrder,
  isStockingReplenishmentOrder,
  normalizeStockingValue,
  MAGNET_SKU_BY_FAMILY,
  merchandiseTotal,
  parseSalesOrderChargeMode,
  type SalesOrderChargeMode,
} from "@integrator-src/sales-order/sales-order-charge-lines";
import { salesOrderIsMegaventoryControlled } from "@integrator-src/sales-order/sales-order-ownership";
import {
  DEFAULT_SALES_ORDER_COUNTRY_CODE,
  normalizeSalesOrderCountryCode,
  normalizeSalesOrderRegionInput,
  regionAfterSalesOrderCountryChange,
  salesOrderRegionField,
} from "@integrator-src/sales-order/sales-order-address-country";
import {
  approvalDocumentPreviewEndpoint,
  fetchInsideSalesApproval,
  linkInsideSalesApproval,
} from "@/pages/inside-sales/insideSalesApprovalsApi";

import { ShipByDatePicker } from "./ShipByDatePicker";
import {
  chicagoTodayIsoDate,
  SHIP_BY_DATE_BEFORE_TODAY_MESSAGE,
  shipByDateFieldHelper,
  shipByDateWriteIsAllowed,
} from "./shipByDatePickerUtils";
import { SalesOrderDetailOverflowMenu } from "./SalesOrderDetailOverflowMenu";
import { SalesOrderDownloadMenu } from "./SalesOrderDownloadMenu";
import { SalesOrderLinkApprovalModal } from "./SalesOrderLinkApprovalModal";
import {
  formatSalesOrderEditorDirtyHint,
  listSalesOrderEditorDirtyFields,
  salesOrderEditorPartyBaselineReady,
} from "./salesOrderEditorDirty";
import { activeSalesOrderQuoteLines, quoteLineStillValid, selectSalesOrderQuotedSkuLine } from "./salesOrderQuotedSkuPrice";
import {
  clampSalesOrderPreviewSidebarWidthPx,
  initialSalesOrderPreviewSidebarWidthPx,
  SALES_ORDER_PREVIEW_SIDEBAR_COLLAPSED_PX,
  writeSalesOrderPreviewSidebarWidthCookie,
} from "./salesOrderPreviewSidebarWidth";
import {
  DEFAULT_SALES_ORDER_SHIPPING_SETTINGS,
  fetchSalesOrderShippingSettings,
  SALES_ORDER_SHIPPING_SETTINGS_QUERY_KEY,
} from "./salesOrderPaymentTermsApi";

import { buildSalesOrderEditorRepDisplays } from "@/lib/salesOrderRepFieldDisplay";
import { resolveSalesOrderApproveBlockedHint } from "./salesOrderApproveBlockedHint";
import {
  resolveSalesOrderRevertToPendingBlockedHint,
  resolveSalesOrderStatusChangeBlockedHint,
} from "./salesOrderStatusChangeBlockedHint";
import {
  formatSalesOrderApproveValidationMessage,
  listSalesOrderApproveValidationMessages,
} from "./salesOrderApproveValidation";

import type {
  PoViewerDocument,
  SalesOrderActivityEntry,
  SalesOrderAddressHistoryResponse,
  SalesOrderAnalysisLogEntry,
  SalesOrderApprovalDocumentsResponse,
  SalesOrderAttachment,
  SalesOrderAttachmentUploadResponse,
  SalesOrderCreateResponse,
  SalesOrderCustomerPoDuplicatesResponse,
  SalesOrderDefaultsResponse,
  SalesOrderDetailResponse,
  SalesOrderEditLockResponse,
  SalesOrderEndUserHistoryResponse,
  SalesOrderEndUserQuotesResponse,
  SalesOrderItemDraft,
  SalesOrderPartyCatalogResponse,
  SalesOrderPartyOption,
  SalesOrderPaymentTermsOptionsResponse,
  SalesOrderPoAnalysisResponse,
  SalesOrderSkuDescriptionResponse,
  SalesOrderSkuOption,
  SalesOrderSkuOptionsResponse,
} from "./createSalesOrderPageModel";
import {
  EMPTY_MAGNET_EXEMPTIONS,
  isSalesOrderEditLockConflict,
  isSalesOrderVersionConflict,
} from "./createSalesOrderPageModel";

import { CreateSalesOrderSaveToast } from "./createSalesOrder/CreateSalesOrderSaveToast";
import {
  type AddressFieldComparison,
  AddressCountryPicker,
  AddressFieldLabel,
  AddressRegionPicker,
  addressesMatch,
  chargeDraftsMatch,
  compareAddressField,
  createItemDraft,
  formatAddressHoverText,
  formatAddressPreview,
  formatDateUs,
  formatLineItemUnitPriceField,
  freshdeskTicketIdFromLink,
  isAnalyzablePoDocument,
  isInvalidLineItemQuantity,
  isShippingItem,
  isSystemChargeItem,
  keepShippingItemsLast,
  labelsForMagnetExemptions,
  lineItemExpandableInputClass,
  lineItemNumericInputClass,
  mergeChargedDrafts,
  normalizeAddressFieldValue,
  normalizeDateInputValue,
  normalizedSalesOrderStatus,
  PartyLookupField,
  salesOrderStatusAppearance,
  serializeSalesOrderAttachments,
  serializeSalesOrderEditorSnapshot,
  SkuLookupField,
  stateFromPartyLabel,
  todayDateOnly,
  trimTrailingWhitespace,
  SalesOrderEditControlGlyph,
} from "./createSalesOrderPageUi";

export function CreateSalesOrderEditorWorkspace() {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const approvalRequestId = searchParams.get("approvalRequestId")?.trim() || null;
  const isEditMode = Boolean(orderId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const meQuery = useIntegratorMe();
  const hubWritesAllowed = useHubModuleWrite("inside_sales");
  const canUseInsideSalesAdminSettings = useHubModuleAdminSettings("inside_sales");
  const isIntegratorAdminUser = isIntegratorAdmin(meQuery.data?.user.roleId);
  const [repSyncError, setRepSyncError] = useState<string | null>(null);
  const saveConfirmationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const attachmentPaneRef = useRef<HTMLDivElement | null>(null);
  const [attachmentDropActive, setAttachmentDropActive] = useState(false);
  const defaultsQuery = useQuery({
    queryKey: ["sales-orders", "new-defaults"],
    queryFn: () => apiFetch<SalesOrderDefaultsResponse>("/api/sales-orders/new-defaults"),
  });
  const paymentTermsQuery = useQuery({
    queryKey: ["sales-orders", "payment-term-options"],
    queryFn: () => apiFetch<SalesOrderPaymentTermsOptionsResponse>("/api/sales-orders/payment-term-options"),
  });
  const shippingSettingsQuery = useQuery({
    queryKey: SALES_ORDER_SHIPPING_SETTINGS_QUERY_KEY,
    queryFn: fetchSalesOrderShippingSettings,
    staleTime: 15_000,
  });
  const partyCatalogQuery = useQuery({
    queryKey: ["sales-orders", "party-catalog"],
    queryFn: () => apiFetch<SalesOrderPartyCatalogResponse>("/api/sales-orders/party-catalog"),
  });
  const skuCatalogQuery = useQuery({
    queryKey: ["sales-orders", "sku-catalog"],
    queryFn: () => apiFetch<SalesOrderSkuOptionsResponse>("/api/sales-orders/sku-catalog"),
  });
  const shippingSettings = shippingSettingsQuery.data ?? DEFAULT_SALES_ORDER_SHIPPING_SETTINGS;
  const shippingSku = shippingSettings.sku;

  const [number, setNumber] = useState("");
  const [status, setStatus] = useState("Pending");
  const [submitted, setSubmitted] = useState("");
  const [shipByDate, setShipByDate] = useState("");
  const [persistedShipByDate, setPersistedShipByDate] = useState("");
  const [customerPurchaseOrderNumber, setCustomerPurchaseOrderNumber] = useState("");
  const [customerPoLookup, setCustomerPoLookup] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("Net30");
  const paymentTermsBeforePilotRef = useRef<string | null>(null);
  const [clientOption, setClientOption] = useState<SalesOrderPartyOption | null>(null);
  const [endUserOption, setEndUserOption] = useState<SalesOrderPartyOption | null>(null);
  const [shipToRecipient, setShipToRecipient] = useState("");
  const [shipToAttention, setShipToAttention] = useState("");
  const [shipToAddressLine1, setShipToAddressLine1] = useState("");
  const [shipToAddressLine2, setShipToAddressLine2] = useState("");
  const [shipToCity, setShipToCity] = useState("");
  const [shipToState, setShipToState] = useState("");
  const [shipToZip, setShipToZip] = useState("");
  const [shipToZipCityMessage, setShipToZipCityMessage] = useState<string | null>(null);
  const [billToZipCityMessage, setBillToZipCityMessage] = useState<string | null>(null);
  const [shipToCountry, setShipToCountry] = useState(DEFAULT_SALES_ORDER_COUNTRY_CODE);
  const [billToRecipient, setBillToRecipient] = useState("");
  const [billToLine1, setBillToLine1] = useState("");
  const [billToLine2, setBillToLine2] = useState("");
  const [billToLine3, setBillToLine3] = useState("");
  const [billToCity, setBillToCity] = useState("");
  const [billToState, setBillToState] = useState("");
  const [billToZip, setBillToZip] = useState("");
  const [billToCountry, setBillToCountry] = useState(DEFAULT_SALES_ORDER_COUNTRY_CODE);
  const [pilotProgram, setPilotProgram] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [trackingRecipientEmails, setTrackingRecipientEmails] = useState<string[]>([]);
  const [invoiceRecipientEmails, setInvoiceRecipientEmails] = useState<string[]>([]);
  const [items, setItems] = useState<SalesOrderItemDraft[]>([createItemDraft()]);
  const [shippingChargeMode, setShippingChargeMode] = useState<SalesOrderChargeMode>("auto");
  const [magnetChargeMode, setMagnetChargeMode] = useState<SalesOrderChargeMode>("auto");
  const [editorBaseline, setEditorBaseline] = useState<string | null>(null);
  const [shouldCaptureEditorBaseline, setShouldCaptureEditorBaseline] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaveConfirmationVisible, setIsSaveConfirmationVisible] = useState(false);
  const [attachments, setAttachments] = useState<SalesOrderAttachment[]>([]);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<string | null>(null);
  const [isPoViewerOpen, setIsPoViewerOpen] = useState(true);
  const [previewSidebarWidthPx, setPreviewSidebarWidthPx] = useState(initialSalesOrderPreviewSidebarWidthPx);
  const [isPreviewSidebarResizing, setIsPreviewSidebarResizing] = useState(false);
  const previewSidebarWidthPxRef = useRef(previewSidebarWidthPx);
  previewSidebarWidthPxRef.current = previewSidebarWidthPx;
  const [poAnalysis, setPoAnalysis] = useState<SalesOrderPoAnalysisResponse | null>(null);
  const [isAnalysisPreviewOpen, setIsAnalysisPreviewOpen] = useState(false);
  const [isAnalysisLogOpen, setIsAnalysisLogOpen] = useState(false);
  const [analysisLog, setAnalysisLog] = useState<SalesOrderAnalysisLogEntry[]>([]);
  const [showRemarksColumn, setShowRemarksColumn] = useState(false);
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [historyTab, setHistoryTab] = useState<"orders" | "quotes" | "activity">("orders");
  const [activityNoteDraft, setActivityNoteDraft] = useState("");
  const [activityNoteError, setActivityNoteError] = useState<string | null>(null);
  const [isLinkApprovalModalOpen, setIsLinkApprovalModalOpen] = useState(false);
  const [linkApprovalError, setLinkApprovalError] = useState<string | null>(null);
  const [loadedOrderId, setLoadedOrderId] = useState<string | null>(null);
  const [loadedOrderVersion, setLoadedOrderVersion] = useState<number | null>(null);
  const [isTakeoverModalOpen, setIsTakeoverModalOpen] = useState(false);
  const autoDescriptionRequestsRef = useRef(new Set<string>());
  const appliedApprovalRequestRef = useRef<string | null>(null);
  const previousIsEditModeRef = useRef(isEditMode);
  const normalizedStatus = normalizedSalesOrderStatus(status);
  const isPendingOrder = normalizedStatus === "pending";
  const isApprovedOrder = normalizedStatus === "approved" || normalizedStatus === "verified";
  const statusAppearance = salesOrderStatusAppearance(status);
  const orderDetailQuery = useQuery({
    queryKey: ["sales-orders", "detail", orderId],
    enabled: isEditMode && orderId != null,
    queryFn: () => apiFetch<SalesOrderDetailResponse>(`/api/sales-orders/${orderId}`),
  });
  const activityNoteMutation = useMutation({
    mutationFn: (note: string) =>
      apiFetch<{ activity: SalesOrderActivityEntry }>(`/api/sales-orders/${orderId}/activity/note`, {
        method: "POST",
        body: { note },
      }),
    onMutate: () => setActivityNoteError(null),
    onSuccess: async () => {
      setActivityNoteDraft("");
      await queryClient.invalidateQueries({ queryKey: ["sales-orders", "detail", orderId] });
    },
    onError: (error) => {
      setActivityNoteError(error instanceof Error ? error.message : "Could not save note.");
    },
  });
  const approvalRequestQuery = useQuery({
    queryKey: ["inside-sales", "approvals", approvalRequestId],
    queryFn: () => fetchInsideSalesApproval(approvalRequestId ?? ""),
    enabled: !isEditMode && approvalRequestId != null,
  });
  const resolvedSalesOrderId = orderDetailQuery.data?.order.id ?? null;
  const megaventoryOrderUrl = buildMegaventorySalesOrderUrl(orderDetailQuery.data?.order.mvId ?? null);
  const lockedRepSyncAt = orderDetailQuery.data?.order.repSyncAt ?? null;
  const lockedRepFreshdeskTicketId = orderDetailQuery.data?.order.repSyncFreshdeskTicketId ?? null;
  const approvalTicketId = orderDetailQuery.data?.order.approvalTicketId ?? null;
  const repSyncMutation = useMutation({
    mutationFn: (salesOrderId: number) => rerunInsideSalesSalesOrderRepSync(salesOrderId),
    onMutate: () => setRepSyncError(null),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["sales-orders", "detail", orderId] });
      if (result.sync.outcome === "failed" && result.sync.assignment?.errors?.length) {
        setRepSyncError(result.sync.assignment.errors.map((error) => error.message).join(" "));
      }
    },
    onError: (error) => {
      setRepSyncError(error instanceof Error ? error.message : String(error));
    },
  });
  const editLockQueryKey = ["sales-orders", "edit-lock", resolvedSalesOrderId] as const;
  const editLockQuery = useQuery({
    queryKey: editLockQueryKey,
    enabled: hubWritesAllowed && isEditMode && resolvedSalesOrderId != null && isPendingOrder,
    queryFn: () =>
      apiFetch<SalesOrderEditLockResponse>(
        `/api/sales-orders/${resolvedSalesOrderId}/edit-lock`,
        { cache: "no-store" },
      ),
    refetchOnWindowFocus: true,
  });
  const editLock = editLockQuery.data?.lock ?? null;
  const acquireEditLockMutation = useMutation({
    mutationFn: () =>
      apiFetch<SalesOrderEditLockResponse>(
        `/api/sales-orders/${resolvedSalesOrderId}/edit-lock/acquire`,
        { method: "POST" },
      ),
    onSuccess: (response) => {
      queryClient.setQueryData(editLockQueryKey, response);
    },
  });
  const takeoverEditLockMutation = useMutation({
    mutationFn: () =>
      apiFetch<SalesOrderEditLockResponse>(
        `/api/sales-orders/${resolvedSalesOrderId}/edit-lock/takeover`,
        { method: "POST" },
      ),
    onSuccess: (response) => {
      queryClient.setQueryData(editLockQueryKey, response);
      setIsTakeoverModalOpen(false);
    },
  });
  const heartbeatEditLockMutation = useMutation({
    mutationFn: (lockToken: string) =>
      apiFetch<SalesOrderEditLockResponse>(
        `/api/sales-orders/${resolvedSalesOrderId}/edit-lock/heartbeat`,
        { method: "POST", body: { lockToken } },
      ),
    onSuccess: (response) => {
      queryClient.setQueryData(editLockQueryKey, response);
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: editLockQueryKey });
    },
  });
  const statusMutation = useMutation({
    mutationFn: async ({ nextStatus }: { nextStatus: "Approved" | "Pending" }) =>
      apiFetch<{
        ok: boolean;
        order: {
          id: number;
          number: string;
          status: string | null;
          version: number;
          locationId: string | null;
          locationAbbreviation: string | null;
        };
      }>(`/api/sales-orders/${resolvedSalesOrderId ?? orderId}/status`, {
        method: "PATCH",
        body: {
          status: nextStatus,
          expectedVersion: loadedOrderVersion,
          lockToken: nextStatus === "Approved" ? editLock?.lockToken : undefined,
        },
      }),
    onSuccess: async (response) => {
      setSubmitError(null);
      setStatus(response.order.status ?? "Pending");
      setLoadedOrderVersion(response.order.version);
      await queryClient.invalidateQueries({ queryKey: ["sales-orders", "overview"] });
      if (orderId) {
        await queryClient.invalidateQueries({ queryKey: ["sales-orders", "detail", orderId] });
      }
    },
  });
  const createApprovalTicketMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ approvalTicketId: string }>(
        `/api/sales-orders/${resolvedSalesOrderId ?? orderId}/approval-ticket`,
        { method: "POST" },
      ),
    onSuccess: async (response) => {
      setLinkApprovalError(null);
      setIsLinkApprovalModalOpen(false);
      if (orderId) {
        queryClient.setQueryData<SalesOrderDetailResponse>(["sales-orders", "detail", orderId], (current) => {
          if (current == null) {
            return current;
          }
          return {
            ...current,
            order: { ...current.order, approvalTicketId: response.approvalTicketId },
          };
        });
        await queryClient.invalidateQueries({ queryKey: ["sales-orders", "detail", orderId] });
      }
      await queryClient.invalidateQueries({ queryKey: ["sales-orders", "overview"] });
      showToast({
        tone: "success",
        message: "Hub approval created. Use View approval to open it.",
      });
    },
    onError: (error) => {
      setLinkApprovalError(error instanceof Error ? error.message : "Could not create an approval task.");
    },
  });
  const linkApprovalMutation = useMutation({
    mutationFn: (reference: string) =>
      apiFetch<{ approvalTicketId: string }>(
        `/api/sales-orders/${resolvedSalesOrderId ?? orderId}/link-approval`,
        {
          method: "POST",
          body: { reference },
        },
      ),
    onSuccess: async (response) => {
      setLinkApprovalError(null);
      setIsLinkApprovalModalOpen(false);
      if (orderId) {
        queryClient.setQueryData<SalesOrderDetailResponse>(["sales-orders", "detail", orderId], (current) => {
          if (current == null) {
            return current;
          }
          return {
            ...current,
            order: { ...current.order, approvalTicketId: response.approvalTicketId },
          };
        });
      }
      await queryClient.invalidateQueries({ queryKey: ["sales-orders", "overview"] });
    },
    onError: (error) => {
      setLinkApprovalError(error instanceof Error ? error.message : "Could not link an approval to this sales order.");
    },
  });
  const salesOrderResourceChannel =
    resolvedSalesOrderId == null ? null : HUB_CACHE_CHANNEL.salesOrderResource(resolvedSalesOrderId);
  useHubCacheStream({
    channels: salesOrderResourceChannel ? [salesOrderResourceChannel] : [],
    enabled: isEditMode && salesOrderResourceChannel != null,
    connectionKey: meQuery.data?.user.id ?? null,
    onEvent: (event) => {
      if (event.phase !== "ready" && event.phase !== "invalidated") {
        return;
      }
      if (!statusMutation.isPending) {
        void queryClient.invalidateQueries({ queryKey: editLockQueryKey });
      }
      void queryClient.invalidateQueries({ queryKey: ["sales-orders", "detail", orderId] });
    },
  });
  function commitCustomerPurchaseOrderNumber(raw: string) {
    const next = trimTrailingWhitespace(raw);
    setCustomerPurchaseOrderNumber(next);
    setCustomerPoLookup(next.trim());
  }
  const customerPoDuplicatesQuery = useQuery({
    queryKey: ["sales-orders", "customer-po-duplicates", customerPoLookup, resolvedSalesOrderId],
    enabled: customerPoLookup.trim() !== "",
    queryFn: () =>
      apiFetch<SalesOrderCustomerPoDuplicatesResponse>("/api/sales-orders/customer-po-duplicates", {
        query: {
          number: customerPoLookup.trim(),
          excludeOrderId: resolvedSalesOrderId ?? undefined,
        },
      }),
  });
  const customerPoDuplicates =
    customerPoLookup.trim() !== "" && customerPurchaseOrderNumber.trim() === customerPoLookup.trim()
      ? (customerPoDuplicatesQuery.data?.orders ?? [])
      : [];
  const endUserHistoryQuery = useQuery({
    queryKey: ["sales-orders", "end-user-history", endUserOption?.fsAccountId, resolvedSalesOrderId],
    enabled: endUserOption?.fsAccountId != null,
    queryFn: () =>
      apiFetch<SalesOrderEndUserHistoryResponse>("/api/sales-orders/end-user-history", {
        query: {
          fsAccountId: endUserOption?.fsAccountId ?? undefined,
          excludeOrderId: resolvedSalesOrderId ?? undefined,
          limit: 60,
        },
        }),
  });
  const endUserQuotesQuery = useQuery({
    queryKey: ["sales-orders", "end-user-quotes", endUserOption?.fsAccountId],
    enabled: endUserOption?.fsAccountId != null,
    queryFn: () =>
      apiFetch<SalesOrderEndUserQuotesResponse>("/api/sales-orders/end-user-quotes", {
        query: {
          fsAccountId: endUserOption?.fsAccountId ?? undefined,
          limit: 50,
        },
      }),
  });
  const addressHistoryQuery = useQuery({
    queryKey: ["sales-orders", "address-history", clientOption?.fsAccountId, endUserOption?.fsAccountId],
    enabled: clientOption?.fsAccountId != null || endUserOption?.fsAccountId != null,
    queryFn: () =>
      apiFetch<SalesOrderAddressHistoryResponse>("/api/sales-orders/address-history", {
        query: {
          clientFsAccountId: clientOption?.fsAccountId ?? undefined,
          endUserFsAccountId: endUserOption?.fsAccountId ?? undefined,
        },
      }),
  });
  const clientCatalog = partyCatalogQuery.data?.clients ?? [];
  const endUserCatalog = partyCatalogQuery.data?.endUsers ?? [];
  const skuCatalog = skuCatalogQuery.data?.options ?? [];
  useEffect(() => {
    const approval = approvalRequestQuery.data;
    if (
      isEditMode ||
      approvalRequestId == null ||
      approval == null ||
      approval.aiStatus !== "ready" ||
      appliedApprovalRequestRef.current === approvalRequestId
    ) {
      return;
    }
    const payload = approval.aiPayload;
    const clientMatch = clientCatalog.find((option) =>
      payload?.clientId
        ? option.fsAccountId === payload.clientId
        : option.label === (payload?.clientName ?? approval.request.clientName),
    );
    const endUserMatch = endUserCatalog.find((option) =>
      payload?.endUserId
        ? option.fsAccountId === payload.endUserId
        : option.label === (payload?.endUserName ?? approval.request.endUserName),
    );
    if (clientMatch) setClientOption(clientMatch);
    if (endUserMatch) setEndUserOption(endUserMatch);
    if (payload?.paymentTerm) setPaymentTerms(payload.paymentTerm);
    if (payload?.comments) setSpecialInstructions(payload.comments);
    if (payload?.lines && payload.lines.length > 0) {
      setItems(
        payload.lines.map((line) =>
          createItemDraft({
            sku: line.sku?.trim() ?? "",
            productDescription: line.description?.trim() ?? "",
            quantity: line.quantity == null ? "1" : String(line.quantity),
            unitPrice: formatLineItemUnitPriceField(line.unitPrice),
          }),
        ),
      );
    }
    appliedApprovalRequestRef.current = approvalRequestId;
  }, [
    approvalRequestId,
    approvalRequestQuery.data,
    clientCatalog,
    endUserCatalog,
    isEditMode,
  ]);
  const preloadError = useMemo(() => {
    const messages: string[] = [];
    if (partyCatalogQuery.isError) {
      messages.push(
        `Client and end user lists failed to load${
          partyCatalogQuery.error instanceof Error ? `: ${partyCatalogQuery.error.message}` : "."
        }`,
      );
    } else if (!partyCatalogQuery.isLoading && clientCatalog.length === 0 && endUserCatalog.length === 0) {
      messages.push("Client and end user lists loaded empty.");
    }
    if (skuCatalogQuery.isError) {
      messages.push(
        `SKU list failed to load${
          skuCatalogQuery.error instanceof Error ? `: ${skuCatalogQuery.error.message}` : "."
        }`,
      );
    } else if (!skuCatalogQuery.isLoading && skuCatalog.length === 0) {
      messages.push("SKU list loaded empty.");
    }
    return messages.length > 0 ? messages.join(" ") : null;
  }, [
    clientCatalog.length,
    endUserCatalog.length,
    partyCatalogQuery.error,
    partyCatalogQuery.isError,
    partyCatalogQuery.isLoading,
    skuCatalog.length,
    skuCatalogQuery.error,
    skuCatalogQuery.isError,
    skuCatalogQuery.isLoading,
  ]);

  useEffect(() => {
    return () => {
      if (saveConfirmationTimerRef.current) {
        clearTimeout(saveConfirmationTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (
      !hubWritesAllowed ||
      !isEditMode ||
      resolvedSalesOrderId == null ||
      !isPendingOrder ||
      statusMutation.isPending ||
      editLockQuery.isLoading ||
      editLockQuery.data?.lock.status !== "available" ||
      acquireEditLockMutation.isPending
    ) {
      return;
    }
    acquireEditLockMutation.mutate();
  }, [
    acquireEditLockMutation,
    editLockQuery.data?.lock.status,
    editLockQuery.isLoading,
    hubWritesAllowed,
    isEditMode,
    isPendingOrder,
    resolvedSalesOrderId,
    statusMutation.isPending,
  ]);

  useEffect(() => {
    const lock = editLockQuery.data?.lock;
    if (!isEditMode || !isPendingOrder || statusMutation.isPending || !lock?.isOwner || !lock.lockToken) {
      return;
    }
    const renew = () => {
      if (!heartbeatEditLockMutation.isPending) {
        heartbeatEditLockMutation.mutate(lock.lockToken as string);
      }
    };
    const interval = window.setInterval(renew, 5 * 60_000);
    const handleFocus = () => renew();
    window.addEventListener("focus", handleFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
    };
  }, [editLockQuery.data?.lock, heartbeatEditLockMutation, isEditMode, isPendingOrder, statusMutation.isPending]);

  useEffect(() => {
    if (editLock?.status !== "held" || !editLock.expiresAt) {
      return;
    }
    const expiryMs = Date.parse(editLock.expiresAt);
    if (!Number.isFinite(expiryMs)) {
      return;
    }
    const delay = Math.max(expiryMs - Date.now() + 250, 250);
    const timeout = window.setTimeout(() => {
      void editLockQuery.refetch();
    }, delay);
    return () => window.clearTimeout(timeout);
  }, [editLock?.expiresAt, editLock?.status, editLockQuery]);

  useEffect(() => {
    if (statusMutation.isPending) {
      return;
    }
    if (editLock?.isOwner === true) {
      setIsTakeoverModalOpen(false);
    }
  }, [editLock?.isOwner, isPendingOrder, statusMutation.isPending]);

  useEffect(() => {
    const wasEditMode = previousIsEditModeRef.current;
    previousIsEditModeRef.current = isEditMode;

    if (isEditMode || !wasEditMode) {
      return;
    }

    setNumber("");
    setStatus("Pending");
    setSubmitted("");
    setShipByDate("");
    setPersistedShipByDate("");
    setCustomerPurchaseOrderNumber("");
    setPaymentTerms("Net30");
    paymentTermsBeforePilotRef.current = null;
    setClientOption(null);
    setEndUserOption(null);
    setShipToRecipient("");
    setShipToAttention("");
    setShipToAddressLine1("");
    setShipToAddressLine2("");
    setShipToCity("");
    setShipToState("");
    setShipToZip("");
    setShipToCountry(DEFAULT_SALES_ORDER_COUNTRY_CODE);
    setBillToRecipient("");
    setBillToLine1("");
    setBillToLine2("");
    setBillToLine3("");
    setBillToCity("");
    setBillToState("");
    setBillToZip("");
    setBillToCountry(DEFAULT_SALES_ORDER_COUNTRY_CODE);
    setPilotProgram(false);
    setSpecialInstructions("");
    setItems([createItemDraft()]);
    setShippingChargeMode("auto");
    setMagnetChargeMode("auto");
    setEditorBaseline(null);
    setShouldCaptureEditorBaseline(false);
    setSubmitError(null);
    setAttachments((current) => {
      current.forEach((attachment) => {
        if (attachment.localUrl) {
          URL.revokeObjectURL(attachment.localUrl);
        }
      });
      return [];
    });
    setSelectedAttachmentId(null);
    setIsPoViewerOpen(true);
    setPoAnalysis(null);
    setIsAnalysisPreviewOpen(false);
    setIsAnalysisLogOpen(false);
    setAnalysisLog([]);
    setShowRemarksColumn(false);
    setHistoryTab("orders");
    setLoadedOrderId(null);
    setLoadedOrderVersion(null);
    setIsTakeoverModalOpen(false);
    autoDescriptionRequestsRef.current.clear();

    void queryClient.invalidateQueries({ queryKey: ["sales-orders", "new-defaults"] });
    void apiFetch<SalesOrderDefaultsResponse>("/api/sales-orders/new-defaults", { cache: "no-store" })
      .then((defaults) => {
        if (previousIsEditModeRef.current) {
          return;
        }
        setSubmitted(defaults.defaultSubmitted ?? "");
      })
      .catch((error) => {
        setSubmitError(error instanceof Error ? error.message : "Could not load new sales order defaults.");
      });
  }, [isEditMode, queryClient]);

  useEffect(() => {
    if (!isEditMode && submitted === "" && defaultsQuery.data?.defaultSubmitted) {
      setSubmitted(defaultsQuery.data.defaultSubmitted);
    }
  }, [defaultsQuery.data?.defaultSubmitted, isEditMode, submitted]);

  useEffect(() => {
    if (!isEditMode || !orderDetailQuery.data || shippingSettingsQuery.isPending || loadedOrderId === orderId) {
      return;
    }

    const detail = orderDetailQuery.data;
    setLoadedOrderVersion(detail.order.version);
    setNumber(detail.order.number ?? "");
    setStatus(detail.order.status ?? "Pending");
    setSubmitted(normalizeDateInputValue(detail.order.submitted));
    const normalizedShipByDate = normalizeDateInputValue(detail.order.shipByDate);
    setShipByDate(normalizedShipByDate);
    setPersistedShipByDate(normalizedShipByDate);
    const loadedCustomerPo = detail.order.customerPurchaseOrderNumber ?? "";
    setCustomerPurchaseOrderNumber(loadedCustomerPo);
    setCustomerPoLookup(loadedCustomerPo.trim());
    const loadedPilot = Boolean(detail.order.pilotProgram);
    const loadedPaymentTerms = detail.order.paymentTerms ?? "Net30";
    paymentTermsBeforePilotRef.current =
      loadedPilot && loadedPaymentTerms !== "Net90" ? loadedPaymentTerms : null;
    setPaymentTerms(loadedPilot ? "Net90" : loadedPaymentTerms);
    setShippingChargeMode(parseSalesOrderChargeMode(detail.order.shippingChargeMode));
    setMagnetChargeMode(parseSalesOrderChargeMode(detail.order.magnetChargeMode));
    setShipToRecipient(detail.order.shipToRecipient ?? "");
    setShipToAttention(detail.order.shipToAttention ?? "");
    setShipToAddressLine1(detail.order.shipToAddressLine1 ?? "");
    setShipToAddressLine2(detail.order.shipToAddressLine2 ?? "");
    setShipToCity(detail.order.shipToCity ?? "");
    setShipToCountry(normalizeSalesOrderCountryCode(detail.order.shipToCountry));
    setShipToState(normalizeSalesOrderRegionInput(detail.order.shipToCountry, detail.order.shipToState));
    setShipToZip(detail.order.shipToZip ?? "");
    setBillToRecipient(detail.order.billToRecipient ?? "");
    setBillToLine1(detail.order.billToLine1 ?? "");
    setBillToLine2(detail.order.billToLine2 ?? "");
    setBillToLine3(detail.order.billToLine3 ?? "");
    setBillToCity(detail.order.billToCity ?? "");
    setBillToCountry(normalizeSalesOrderCountryCode(detail.order.billToCountry));
    setBillToState(normalizeSalesOrderRegionInput(detail.order.billToCountry, detail.order.billToState));
    setBillToZip(detail.order.billToZip ?? "");
    setPilotProgram(Boolean(detail.order.pilotProgram));
    setSpecialInstructions(detail.order.specialInstructions ?? "");
    setTrackingRecipientEmails(detail.order.trackingRecipientEmails ?? []);
    setInvoiceRecipientEmails(detail.order.invoiceRecipientEmails ?? []);
    const nextItems = detail.items.length > 0
      ? keepShippingItemsLast(detail.items.map((item) => createItemDraft({
            sku: item.sku,
            templateId: item.templateId,
            productDescription: item.description ?? "",
            productDescriptionError: "",
            quantity: String(item.quantity),
            unitPrice: formatLineItemUnitPriceField(item.unitPrice),
            remark: item.remark ?? "",
            showRemark: Boolean(item.remark),
            megaventoryId: "",
            origin: item.origin ?? "user",
          })), shippingSku)
      : [createItemDraft()];
    setItems(nextItems);
    nextItems.forEach((item) => {
      if (item.sku.trim() !== "" && item.productDescription.trim() === "") {
        const requestKey = `${detail.order.id}:${item.draftId}:${item.sku.trim()}`;
        autoDescriptionRequestsRef.current.add(requestKey);
        void populateSkuDescription(item.draftId, item.sku, {
          fallbackDescription: skuCatalog.find((option) => option.sku === item.sku)?.description ?? item.productDescription,
          baselineRequestKey: requestKey,
        });
      }
    });
    setShowRemarksColumn(detail.items.some((item) => Boolean(item.remark)));
    setAttachments((current) => {
      current.forEach((attachment) => {
        if (attachment.localUrl) {
          URL.revokeObjectURL(attachment.localUrl);
        }
      });
      return detail.order.attachments ?? [];
    });
    setSelectedAttachmentId(detail.order.attachments?.[0]?.id ?? null);
    setIsPoViewerOpen(true);
    setLoadedOrderId(orderId ?? null);
    setShouldCaptureEditorBaseline(true);
  }, [isEditMode, loadedOrderId, orderDetailQuery.data, orderId, shippingSettingsQuery.isPending, shippingSku]);

  useEffect(() => {
    if (!isEditMode || !orderDetailQuery.data) {
      return;
    }

    const detail = orderDetailQuery.data;
    const clientMatch =
      clientCatalog.find((option) => option.fsAccountId === detail.order.clientFsAccountId) ?? null;
    const endUserMatch =
      endUserCatalog.find((option) => option.fsAccountId === detail.order.endUserFsAccountId) ?? null;

    setClientOption(clientMatch);
    setEndUserOption(endUserMatch);
  }, [clientCatalog, endUserCatalog, isEditMode, orderDetailQuery.data]);

  const approvalDocumentsQuery = useQuery({
    queryKey: ["sales-orders", "approval-documents", resolvedSalesOrderId],
    enabled: resolvedSalesOrderId != null,
    queryFn: () =>
      apiFetch<SalesOrderApprovalDocumentsResponse>(`/api/sales-orders/${resolvedSalesOrderId}/approval-documents`),
  });

  const poViewerDocuments = useMemo(() => {
    const documents: PoViewerDocument[] = [];
    for (const attachment of attachments) {
      documents.push({
        id: attachment.id,
        kind: "order_attachment",
        kindLabel: "Order attachment",
        fileName: attachment.fileName,
        mimeType: attachment.mimeType,
        downloadUrl: attachment.downloadUrl || null,
        localUrl: attachment.localUrl,
        localFile: attachment.localFile,
        textBody: null,
        orderAttachment: attachment,
      });
    }
    for (const document of approvalDocumentsQuery.data?.documents ?? []) {
      documents.push({
        id: document.id,
        kind: document.kind,
        kindLabel: document.kindLabel,
        fileName: document.fileName,
        mimeType: document.contentType,
        downloadUrl: document.downloadUrl,
        textBody: document.textBody,
      });
    }
    if (!isEditMode && approvalRequestId != null && approvalRequestQuery.data != null) {
      const request = approvalRequestQuery.data.request;
      const sourceText = request.sourceText?.trim() ?? "";
      if (sourceText !== "" && !(approvalDocumentsQuery.data?.documents ?? []).some((document) => document.kind === "email_thread")) {
        documents.push({
          id: "approval-request-ticket",
          kind: "email_thread",
          kindLabel: "Freshdesk ticket",
          fileName: request.sourceTicketNumber
            ? `Freshdesk ${request.sourceTicketNumber}.txt`
            : "Freshdesk ticket.txt",
          mimeType: "text/plain",
          downloadUrl: null,
          textBody: sourceText,
        });
      }
      for (const document of approvalRequestQuery.data.documents) {
        documents.push({
          id: `approval-request-file-${document.id}`,
          kind: "approval_attachment",
          kindLabel: "Freshdesk attachment",
          fileName: document.fileName,
          mimeType: document.contentType,
          downloadUrl: approvalDocumentPreviewEndpoint(approvalRequestId, document.id),
          textBody: null,
        });
      }
    }
    return documents;
  }, [
    approvalDocumentsQuery.data?.documents,
    approvalRequestId,
    approvalRequestQuery.data,
    attachments,
    isEditMode,
  ]);

  const selectedPoDocument =
    poViewerDocuments.find((poDocument) => poDocument.id === selectedAttachmentId) ?? poViewerDocuments[0] ?? null;
  const approvalFreshdeskTicketId =
    approvalDocumentsQuery.data?.freshdeskTicketId ??
    freshdeskTicketIdFromLink(approvalDocumentsQuery.data?.freshdeskLink) ??
    freshdeskTicketIdFromLink(approvalRequestQuery.data?.request.sourceTicketUrl);

  const selectedPoDocumentPreviewQuery = useQuery({
    queryKey: ["sales-orders", "po-viewer-preview", selectedPoDocument?.id, selectedPoDocument?.downloadUrl],
    enabled:
      selectedPoDocument != null &&
      selectedPoDocument.textBody == null &&
      selectedPoDocument.localUrl == null &&
      selectedPoDocument.downloadUrl != null,
    queryFn: async () => {
      const result = await apiFetchBlob(selectedPoDocument?.downloadUrl ?? "");
      return URL.createObjectURL(result.blob);
    },
  });
  const selectedPoDocumentPreviewUrl = selectedPoDocument?.localUrl ?? selectedPoDocumentPreviewQuery.data ?? null;

  useEffect(() => {
    return () => {
      if (selectedPoDocumentPreviewQuery.data) {
        URL.revokeObjectURL(selectedPoDocumentPreviewQuery.data);
      }
    };
  }, [selectedPoDocumentPreviewQuery.data]);

  useEffect(() => {
    if (poViewerDocuments.length === 0) {
      if (selectedAttachmentId != null) {
        setSelectedAttachmentId(null);
      }
      return;
    }
    if (selectedAttachmentId != null && poViewerDocuments.some((poDocument) => poDocument.id === selectedAttachmentId)) {
      return;
    }
    setSelectedAttachmentId(poViewerDocuments[0]?.id ?? null);
  }, [poViewerDocuments, selectedAttachmentId]);

  const canEditOrder =
    hubWritesAllowed && (!isEditMode || (isPendingOrder && !statusMutation.isPending && editLock?.isOwner === true));
  const canDownloadSalesOrderPdf = insideSalesUserMayDownloadSalesOrderPdf({
    isPendingOrder,
    isIntegratorAdmin: isIntegratorAdminUser,
    isInsideSalesAdmin: canUseInsideSalesAdminSettings,
  });
  const isMegaventoryControlled = salesOrderIsMegaventoryControlled(orderDetailQuery.data?.order.mvId);
  const canEditMvControlledFields = canEditOrder && !isMegaventoryControlled;
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);

  function handleClientSelect(option: SalesOrderPartyOption | null) {
    setClientOption(option);
    const accountsPayableEmail = option?.accountsPayableEmail?.trim() ?? "";
    if (accountsPayableEmail !== "") {
      setInvoiceRecipientEmails([accountsPayableEmail]);
    }
  }

  function handleEndUserSelect(option: SalesOrderPartyOption | null) {
    setEndUserOption(option);
    const salesManagerEmail = option?.salesManagerEmail?.trim() ?? "";
    if (salesManagerEmail !== "") {
      setTrackingRecipientEmails([salesManagerEmail]);
    }
  }

  function addLocalAttachments(files: FileList | File[]) {
    const uploadableFiles = Array.from(files).filter((file) => isUploadableFile(file));
    const nextAttachments = uploadableFiles.map((file) => ({
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      bucket: "",
      path: "",
      fileName: file.name,
      mimeType: file.type || null,
      size: file.size,
      uploadedAt: new Date().toISOString(),
      kind: "customer_po",
      downloadUrl: "",
      localUrl: URL.createObjectURL(file),
      localFile: file,
    }));
    if (nextAttachments.length === 0) {
      return;
    }
    setAttachments((current) => [...current, ...nextAttachments]);
    setSelectedAttachmentId(nextAttachments[0]?.id ?? selectedAttachmentId);
    setIsPoViewerOpen(true);
    setPoAnalysis(null);
    setSubmitError(null);
  }

  const attachmentDropDisabled = !canEditOrder || isUploadingAttachments;

  function showAttachmentDropHighlight() {
    attachmentPaneRef.current?.setAttribute("data-attachment-drop", "true");
    setAttachmentDropActive((current) => (current ? current : true));
  }

  function clearAttachmentDropHighlight() {
    attachmentPaneRef.current?.removeAttribute("data-attachment-drop");
    setAttachmentDropActive(false);
  }

  function onAttachmentPaneDragEnter(event: ReactDragEvent<HTMLDivElement>) {
    if (!dragEventHasFiles(event.dataTransfer.types)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (attachmentDropDisabled) {
      return;
    }
    showAttachmentDropHighlight();
  }

  function onAttachmentPaneDragOver(event: ReactDragEvent<HTMLDivElement>) {
    if (!dragEventHasFiles(event.dataTransfer.types)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = attachmentDropDisabled ? "none" : "copy";
    if (attachmentDropDisabled) {
      return;
    }
    showAttachmentDropHighlight();
  }

  function onAttachmentPaneDragLeave(event: ReactDragEvent<HTMLDivElement>) {
    const related = event.relatedTarget;
    if (related instanceof Node && event.currentTarget.contains(related)) {
      return;
    }
    clearAttachmentDropHighlight();
  }

  function onAttachmentPaneDrop(event: ReactDragEvent<HTMLDivElement>) {
    if (!dragEventHasFiles(event.dataTransfer.types)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    clearAttachmentDropHighlight();
    if (attachmentDropDisabled) {
      return;
    }
    const dropped = salesOrderAttachmentFilesFromDrop(event.dataTransfer.files);
    if (dropped.length === 0) {
      return;
    }
    addLocalAttachments(dropped);
  }

  useEffect(() => {
    function clearHighlight() {
      attachmentPaneRef.current?.removeAttribute("data-attachment-drop");
      setAttachmentDropActive(false);
    }
    window.addEventListener("dragend", clearHighlight);
    window.addEventListener("drop", clearHighlight);
    return () => {
      window.removeEventListener("dragend", clearHighlight);
      window.removeEventListener("drop", clearHighlight);
    };
  }, []);

  async function uploadPendingAttachments(currentAttachments: SalesOrderAttachment[]): Promise<SalesOrderAttachment[]> {
    const pending = currentAttachments.filter((attachment) => attachment.localFile != null);
    if (pending.length === 0) {
      return currentAttachments;
    }

    setIsUploadingAttachments(true);
    try {
      const formData = new FormData();
      formData.append("orderNumber", number || "pending");
      pending.forEach((attachment) => {
        if (attachment.localFile) {
          formData.append("file", attachment.localFile, attachment.fileName);
        }
      });
      const response = await apiUploadFormData<SalesOrderAttachmentUploadResponse>("/api/sales-orders/attachments", formData);
      const uploadedByIndex = response.attachments;
      let uploadIndex = 0;
      const next = currentAttachments.map((attachment) => {
        if (!attachment.localFile) {
          return attachment;
        }
        const uploaded = uploadedByIndex[uploadIndex++];
        if (!uploaded) {
          throw new Error(`Could not upload ${attachment.fileName}.`);
        }
        return {
          ...uploaded,
          localUrl: attachment.localUrl,
        };
      });
      setAttachments(next);
      setSelectedAttachmentId((current) => {
        if (current && next.some((attachment) => attachment.id === current)) {
          return current;
        }
        return next[0]?.id ?? null;
      });
      return next;
    } catch (error) {
      const rawMessage = error instanceof Error ? error.message : "Could not upload attachments.";
      const normalizedMessage = rawMessage.trim().toLowerCase().replace(/[.!?]+$/g, "");
      const message =
        normalizedMessage === "not found" || normalizedMessage.endsWith(" not found")
          ? "Attachment upload returned Not found. Restart local:prodish so the API picks up the new attachment route; if it still happens after restart, confirm the Supabase Storage bucket sales-order-attachment exists in this project."
          : rawMessage;
      throw new Error(`Could not upload attachments before saving the order. ${message}`);
    } finally {
      setIsUploadingAttachments(false);
    }
  }

  function showSaveConfirmation() {
    if (saveConfirmationTimerRef.current) {
      clearTimeout(saveConfirmationTimerRef.current);
    }
    setIsSaveConfirmationVisible(true);
    saveConfirmationTimerRef.current = setTimeout(() => {
      setIsSaveConfirmationVisible(false);
      saveConfirmationTimerRef.current = null;
    }, 4_000);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const uploadedAttachments = await uploadPendingAttachments(attachments);
      return apiFetch<SalesOrderCreateResponse>(
        isEditMode
          ? `/api/sales-orders/${resolvedSalesOrderId ?? orderId}`
          : "/api/sales-orders/create",
        {
          method: isEditMode ? "PATCH" : "POST",
          body: {
            expectedVersion: isEditMode ? loadedOrderVersion : undefined,
            lockToken: isEditMode ? editLock?.lockToken : undefined,
            number,
            submitted: isEditMode ? undefined : submitted || defaultsQuery.data?.defaultSubmitted || todayDateOnly(),
            shipByDate: shipByDate || null,
            customerPurchaseOrderNumber,
            paymentTerms,
            clientFsAccountId: clientOption?.fsAccountId ?? null,
            endUserFsAccountId: endUserOption?.fsAccountId ?? null,
            shipToRecipient,
            shipToAttention,
            shipToAddressLine1,
            shipToAddressLine2,
            shipToCity,
            shipToState,
            shipToZip,
            shipToCountry,
            billToRecipient,
            billToLine1,
            billToLine2,
            billToLine3,
            billToCity,
            billToState,
            billToZip,
            billToCountry,
            pilotProgram,
            specialInstructions,
            shippingChargeMode,
            magnetChargeMode,
            trackingRecipientEmails,
            invoiceRecipientEmails,
            attachments: serializeSalesOrderAttachments(uploadedAttachments),
            items: items.filter((item) => !item.waivedPreview).map((item) => ({
              sku: item.sku,
              productDescription: item.productDescription,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              remark: item.remark,
              origin: item.origin,
            })),
          },
        },
      );
    },
    onSuccess: async (response) => {
      setSubmitError(null);
      setLoadedOrderVersion(response.order.version);
      await queryClient.invalidateQueries({ queryKey: ["sales-orders", "overview"] });
      if ((response.customerReingestAccountIds?.length ?? 0) > 0) {
        await queryClient.invalidateQueries({ queryKey: ["sales-orders", "party-catalog"] });
      }
      if (isEditMode && orderId) {
        await queryClient.invalidateQueries({ queryKey: ["sales-orders", "detail", orderId] });
        showSaveConfirmation();
        setShouldCaptureEditorBaseline(true);
        return;
      }
      if (approvalRequestId) {
        try {
          await linkInsideSalesApproval(approvalRequestId, { salesOrderId: String(response.order.id) });
          await queryClient.invalidateQueries({ queryKey: ["inside-sales", "approvals"] });
        } catch (error) {
          setSubmitError(
            `The sales order was saved, but it could not be linked to the approval request: ${
              error instanceof Error ? error.message : "Unknown error"
            }`,
          );
          navigate(`/inside-sales/sales-orders/${response.order.id}`);
          return;
        }
      }
      navigate("/inside-sales/sales-orders");
    },
  });

  const analyzePoMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPoDocument || !selectedPoDocumentPreviewUrl || !isAnalyzablePoDocument(selectedPoDocument)) {
        throw new Error("Select a PDF or image attachment before running analysis.");
      }
      const blob = await fetch(selectedPoDocumentPreviewUrl).then((response) => response.blob());
      const formData = new FormData();
      formData.append("file", blob, selectedPoDocument.fileName);
      return apiUploadFormData<SalesOrderPoAnalysisResponse>("/api/sales-orders/analyze-po", formData);
    },
    onSuccess: (result) => {
      setPoAnalysis(result);
      setIsAnalysisPreviewOpen(true);
      setAnalysisLog((current) => [
        {
          id: Date.now(),
          timestamp: new Date().toLocaleString(),
          level: "info",
          message: `PO analysis completed${result.fileName ? ` for ${result.fileName}` : ""}.`,
        },
        ...current,
      ]);
    },
    onError: (error) => {
      setAnalysisLog((current) => [
        {
          id: Date.now(),
          timestamp: new Date().toLocaleString(),
          level: "error",
          message: error instanceof Error ? error.message : "PO analysis failed.",
        },
        ...current,
      ]);
      setIsAnalysisLogOpen(true);
    },
  });

  async function reloadLatestOrderAfterConflict() {
    const shouldReload = window.confirm(
      "Reload the latest saved order? Any unsaved changes in this page will be discarded.",
    );
    if (!shouldReload) {
      return;
    }
    const result = await orderDetailQuery.refetch();
    if (!result.data) {
      setSubmitError("Could not reload the latest sales order.");
      return;
    }
    setLoadedOrderId(null);
    setSubmitError(null);
    saveMutation.reset();
    statusMutation.reset();
  }

  function updateItem(index: number, patch: Partial<SalesOrderItemDraft>) {
    setItems((current) => keepShippingItemsLast(
      current.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)),
      shippingSku,
    ));
  }

  function updateItemById(draftId: string, patch: Partial<SalesOrderItemDraft>) {
    setItems((current) => keepShippingItemsLast(
      current.map((item) => (item.draftId === draftId ? { ...item, ...patch } : item)),
      shippingSku,
    ));
  }

  function addItem() {
    if (!canEditOrder) {
      return;
    }
    setItems((current) => {
      const firstShippingIndex = current.findIndex((item) => isShippingItem(item, shippingSku));
      const insertAt = firstShippingIndex === -1 ? current.length : firstShippingIndex;
      return [
        ...current.slice(0, insertAt),
        createItemDraft(),
        ...current.slice(insertAt),
      ];
    });
  }

  function removeItem(index: number) {
    setItems((current) => {
      if (current.length === 1) {
        return isShippingItem(current[0], shippingSku) ? [createItemDraft()] : current;
      }
      return current.filter((_, itemIndex) => itemIndex !== index);
    });
  }

  function moveItem(draggedId: string, targetId: string) {
    if (draggedId === targetId) return;
    setItems((current) => {
      const draggedItem = current.find((item) => item.draftId === draggedId);
      if (!draggedItem || isSystemChargeItem(draggedItem)) return current;

      const shippingItems = current.filter((item) => isSystemChargeItem(item));
      const movableItems = current.filter((item) => !isSystemChargeItem(item));
      const sourceIndex = movableItems.findIndex((item) => item.draftId === draggedId);
      const targetIndex = movableItems.findIndex((item) => item.draftId === targetId);
      if (sourceIndex === -1) return current;

      const reordered = [...movableItems];
      const [moved] = reordered.splice(sourceIndex, 1);
      reordered.splice(targetIndex === -1 ? reordered.length : targetIndex, 0, moved);
      return [...reordered, ...shippingItems];
    });
  }

  useEffect(() => {
    let cancelled = false;
    const handle = window.setTimeout(() => {
      void checkSalesOrderZipCities({
        shipTo: { zip: shipToZip, city: shipToCity, state: shipToState, country: shipToCountry },
        billTo: { zip: billToZip, city: billToCity, state: billToState, country: billToCountry },
      })
        .then((result) => {
          if (cancelled) return;
          setShipToZipCityMessage(result.shipTo);
          setBillToZipCityMessage(result.billTo);
        })
        .catch(() => {
          if (cancelled) return;
          setShipToZipCityMessage(null);
          setBillToZipCityMessage(null);
        });
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [
    billToCity,
    billToCountry,
    billToState,
    billToZip,
    shipToCity,
    shipToCountry,
    shipToState,
    shipToZip,
  ]);

  async function submitOrder() {
    if (!hubWritesAllowed) {
      setSubmitError("Inside Sales write access is required to save sales orders.");
      return;
    }
    if (isEditMode && resolvedSalesOrderId == null) {
      setSubmitError("Sales order details have not finished loading yet.");
      return;
    }
    if (isEditMode && !canEditOrder) {
      setSubmitError(
        !isPendingOrder
          ? "Only Pending orders can be edited."
          : editLock?.holder
            ? `${editLock.holder.displayName} currently controls editing for this order.`
            : "Editing access has not been secured yet.",
      );
      return;
    }
    if (attachments.length === 0) {
      const shouldContinue = window.confirm(`No PO or attachment has been added yet. ${isEditMode ? "Save changes" : "Create the draft order"} anyway?`);
      if (!shouldContinue) {
        return;
      }
    }
    if (clientOption?.fsAccountId == null && clientOption != null) {
      setSubmitError("Selected client is missing an FS account id.");
      return;
    }
    if (endUserOption?.fsAccountId == null && endUserOption != null) {
      setSubmitError("Selected end user is missing an FS account id.");
      return;
    }
    if (!shipByDateWriteIsAllowed(shipByDate, persistedShipByDate, chicagoTodayIsoDate())) {
      setSubmitError(SHIP_BY_DATE_BEFORE_TODAY_MESSAGE);
      return;
    }
    try {
      const zipCheck = await checkSalesOrderZipCities({
        shipTo: { zip: shipToZip, city: shipToCity, state: shipToState, country: shipToCountry },
        billTo: { zip: billToZip, city: billToCity, state: billToState, country: billToCountry },
      });
      setShipToZipCityMessage(zipCheck.shipTo);
      setBillToZipCityMessage(zipCheck.billTo);
    } catch {
      setShipToZipCityMessage(null);
      setBillToZipCityMessage(null);
    }
    setSubmitError(null);
    saveMutation.mutate();
  }

  function approveOrder() {
    if (!isEditMode || resolvedSalesOrderId == null) {
      setSubmitError("Sales order details have not finished loading yet.");
      return;
    }
    if (!isPendingOrder) {
      setSubmitError("Only Pending sales orders can be approved.");
      return;
    }
    if (!canEditOrder || !editLock?.lockToken) {
      setSubmitError(
        editLock?.holder
          ? `${editLock.holder.displayName} currently controls editing for this order.`
          : "Editing access has not been secured yet.",
      );
      return;
    }
    if (approveValidationMessage) {
      setSubmitError(approveValidationMessage);
      return;
    }
    if (statusChangeBlockedHint) {
      setSubmitError(statusChangeBlockedHint);
      return;
    }
    setSubmitError(null);
    statusMutation.mutate({ nextStatus: "Approved" });
  }

  function revertToPending() {
    if (!hubWritesAllowed) {
      setSubmitError("Inside Sales write access is required to edit sales orders.");
      return;
    }
    if (!isEditMode || resolvedSalesOrderId == null) {
      setSubmitError("Sales order details have not finished loading yet.");
      return;
    }
    if (!isApprovedOrder) {
      setSubmitError("Only Approved sales orders can be reverted to Pending.");
      return;
    }
    const shouldRevert = window.confirm("Revert this sales order to Pending?");
    if (!shouldRevert) {
      return;
    }
    setSubmitError(null);
    statusMutation.mutate({ nextStatus: "Pending" });
  }

  function runPoAnalysis() {
    if (!selectedPoDocument || !isAnalyzablePoDocument(selectedPoDocument)) {
      setAnalysisLog((current) => [
        {
          id: Date.now(),
          timestamp: new Date().toLocaleString(),
          level: "error",
          message: "Select a PDF or image attachment before running analysis.",
        },
        ...current,
      ]);
      setIsAnalysisLogOpen(true);
      return;
    }
    const confirmed = window.confirm(
      `Analyze ${selectedPoDocument.fileName || "this PO PDF"} with AI and refresh the analysis preview?`,
    );
    if (!confirmed) {
      return;
    }
    setAnalysisLog((current) => [
      {
        id: Date.now(),
        timestamp: new Date().toLocaleString(),
        level: "info",
        message: `Starting PO analysis for ${selectedPoDocument.fileName}.`,
      },
      ...current,
    ]);
    analyzePoMutation.mutate();
  }

  function applyPoAnalysis() {
    if (!poAnalysis || !canEditMvControlledFields) {
      return;
    }

    commitCustomerPurchaseOrderNumber(poAnalysis.extracted.customerPo ?? "");
    setSpecialInstructions(poAnalysis.extracted.specialInstructions ?? "");

    const matchedClient =
      clientCatalog.find((option) => option.fsAccountId === (poAnalysis.extracted.clientFsAccountId ?? null)) ??
      clientCatalog.find((option) => option.label === (poAnalysis.extracted.clientName ?? ""));
    setClientOption(matchedClient ?? null);
    if (matchedClient?.accountsPayableEmail?.trim()) {
      setInvoiceRecipientEmails([matchedClient.accountsPayableEmail.trim()]);
    }

    const matchedEndUser =
      endUserCatalog.find((option) => option.fsAccountId === (poAnalysis.extracted.endUserFsAccountId ?? null)) ??
      endUserCatalog.find((option) => option.label === (poAnalysis.extracted.endUserName ?? ""));
    setEndUserOption(matchedEndUser ?? null);
    if (matchedEndUser?.salesManagerEmail?.trim()) {
      setTrackingRecipientEmails([matchedEndUser.salesManagerEmail.trim()]);
    }

    setBillToRecipient(poAnalysis.extracted.billTo.recipient ?? "");
    setBillToLine1(poAnalysis.extracted.billTo.line1 ?? "");
    setBillToLine2(poAnalysis.extracted.billTo.line2 ?? "");
    setBillToLine3(poAnalysis.extracted.billTo.line3 ?? "");
    setBillToCity(poAnalysis.extracted.billTo.city ?? "");
    setBillToCountry(normalizeSalesOrderCountryCode(poAnalysis.extracted.billTo.country));
    setBillToState(normalizeSalesOrderRegionInput(poAnalysis.extracted.billTo.country, poAnalysis.extracted.billTo.state));
    setBillToZip(poAnalysis.extracted.billTo.zip ?? "");

    setShipToRecipient(poAnalysis.extracted.shipTo.recipient ?? "");
    setShipToAttention(poAnalysis.extracted.shipTo.attention ?? "");
    setShipToAddressLine1(poAnalysis.extracted.shipTo.line1 ?? "");
    setShipToAddressLine2(poAnalysis.extracted.shipTo.line2 ?? "");
    setShipToCity(poAnalysis.extracted.shipTo.city ?? "");
    setShipToCountry(normalizeSalesOrderCountryCode(poAnalysis.extracted.shipTo.country));
    setShipToState(normalizeSalesOrderRegionInput(poAnalysis.extracted.shipTo.country, poAnalysis.extracted.shipTo.state));
    setShipToZip(poAnalysis.extracted.shipTo.zip ?? "");

    const nextItems = keepShippingItemsLast(poAnalysis.extracted.items.map((item) => {
      const matchedCatalogSku = item.matchedSku
        ? skuCatalog.find((option) => option.sku === item.matchedSku) ?? null
        : null;
      return createItemDraft({
        sku: item.matchedSku ?? item.rawSku ?? "",
        templateId: matchedCatalogSku?.templateId ?? null,
        productDescription: matchedCatalogSku?.description ?? "",
        productDescriptionError: "",
        quantity: item.quantity != null ? String(item.quantity) : "1",
        unitPrice: formatLineItemUnitPriceField(item.unitPrice),
        remark: "",
        showRemark: false,
        megaventoryId: matchedCatalogSku?.megaventoryId ?? "",
      });
    }), shippingSku);
    if (nextItems.length > 0) {
      setItems(nextItems);
      nextItems.forEach((item) => {
        if (item.sku.trim() !== "") {
          void populateSkuDescription(item.draftId, item.sku, { fallbackDescription: item.productDescription });
        }
      });
    }

    setAnalysisLog((current) => [
      {
        id: Date.now(),
        timestamp: new Date().toLocaleString(),
        level: "info",
        message: `Applied analysis results to the sales order form${poAnalysis.fileName ? ` from ${poAnalysis.fileName}` : ""}.`,
      },
      ...current,
    ]);
    setIsAnalysisPreviewOpen(false);
  }

  function applyPreviousBillTo() {
    const previousBillTo = addressHistoryQuery.data?.clientBillTo ?? poAnalysis?.historicalContext.clientBillTo ?? null;
    if (!previousBillTo) {
      return;
    }

    setBillToRecipient(previousBillTo.recipient ?? "");
    setBillToLine1(previousBillTo.line1 ?? "");
    setBillToLine2(previousBillTo.line2 ?? "");
    setBillToLine3(previousBillTo.line3 ?? "");
    setBillToCity(previousBillTo.city ?? "");
    setBillToCountry(normalizeSalesOrderCountryCode(previousBillTo.country));
    setBillToState(normalizeSalesOrderRegionInput(previousBillTo.country, previousBillTo.state));
    setBillToZip(previousBillTo.zip ?? "");
  }

  function clearBillTo() {
    setBillToRecipient("");
    setBillToLine1("");
    setBillToLine2("");
    setBillToLine3("");
    setBillToCity("");
    setBillToState("");
    setBillToZip("");
    setBillToCountry(DEFAULT_SALES_ORDER_COUNTRY_CODE);
  }

  function applyShipToSgs() {
    setShipToRecipient("Smart Grid Solutions");
    setShipToAttention("");
    setShipToAddressLine1("2400 Grand Ave Pkwy");
    setShipToAddressLine2("#105");
    setShipToCity("Austin");
    setShipToState("TX");
    setShipToZip("78728");
    setShipToCountry(DEFAULT_SALES_ORDER_COUNTRY_CODE);
  }

  function clearShipTo() {
    setShipToRecipient("");
    setShipToAttention("");
    setShipToAddressLine1("");
    setShipToAddressLine2("");
    setShipToCity("");
    setShipToState("");
    setShipToZip("");
    setShipToCountry(DEFAULT_SALES_ORDER_COUNTRY_CODE);
  }

  const activePreviousBillTo = addressHistoryQuery.data?.clientBillTo ?? poAnalysis?.historicalContext.clientBillTo ?? null;
  const activePreviousShipTo = addressHistoryQuery.data?.endUserShipTo ?? poAnalysis?.historicalContext.endUserShipTo ?? null;
  const previousBillToHover = formatAddressHoverText(activePreviousBillTo);
  const previousShipToHover = formatAddressHoverText(activePreviousShipTo);
  const billToComparisonLabel = clientOption?.label ? `${clientOption.label} billing address` : "client billing address";
  const shipToComparisonLabel = endUserOption?.label ? `${endUserOption.label} shipping address` : "end user shipping address";
  const billToRecipientComparison = compareAddressField(billToRecipient, activePreviousBillTo?.recipient, activePreviousBillTo, billToComparisonLabel);
  const billToLine1Comparison = compareAddressField(billToLine1, activePreviousBillTo?.line1, activePreviousBillTo, billToComparisonLabel);
  const billToLine2Comparison = compareAddressField(billToLine2, activePreviousBillTo?.line2, activePreviousBillTo, billToComparisonLabel);
  const billToLine3Comparison = compareAddressField(billToLine3, activePreviousBillTo?.line3, activePreviousBillTo, billToComparisonLabel);
  const billToCityComparison = compareAddressField(billToCity, activePreviousBillTo?.city, activePreviousBillTo, billToComparisonLabel);
  const billToStateComparison = compareAddressField(billToState, activePreviousBillTo?.state, activePreviousBillTo, billToComparisonLabel);
  const billToZipComparison = compareAddressField(billToZip, activePreviousBillTo?.zip, activePreviousBillTo, billToComparisonLabel);
  const billToCountryComparison = compareAddressField(billToCountry, activePreviousBillTo?.country, activePreviousBillTo, billToComparisonLabel);
  const shipToRecipientComparison = compareAddressField(shipToRecipient, activePreviousShipTo?.recipient, activePreviousShipTo, shipToComparisonLabel);
  const shipToAttentionComparison = compareAddressField(shipToAttention, activePreviousShipTo?.attention, activePreviousShipTo, shipToComparisonLabel);
  const shipToLine1Comparison = compareAddressField(shipToAddressLine1, activePreviousShipTo?.line1, activePreviousShipTo, shipToComparisonLabel);
  const shipToLine2Comparison = compareAddressField(shipToAddressLine2, activePreviousShipTo?.line2, activePreviousShipTo, shipToComparisonLabel);
  const shipToCityComparison = compareAddressField(shipToCity, activePreviousShipTo?.city, activePreviousShipTo, shipToComparisonLabel);
  const endUserState = endUserOption?.state?.trim() || stateFromPartyLabel(endUserOption?.label);
  const shipToStatePreviousComparison = compareAddressField(shipToState, activePreviousShipTo?.state, activePreviousShipTo, shipToComparisonLabel);
  const shipToStateDiffersFromEndUserState =
    endUserState != null &&
    shipToState.trim() !== "" &&
    normalizeAddressFieldValue(shipToState) !== normalizeAddressFieldValue(endUserState);
  const shipToStateComparison: AddressFieldComparison = shipToStateDiffersFromEndUserState
    ? {
        tone: "diff",
        title: `Does not match selected end user state.\nSelected end user: ${endUserOption?.label ?? "End user"}\nExpected state: ${endUserState}`,
      }
    : endUserState != null && shipToState.trim() !== "" && shipToStatePreviousComparison.tone === "none"
      ? {
          tone: "match",
          title: `Matches selected end user state.\nSelected end user: ${endUserOption?.label ?? "End user"}\nState: ${endUserState}`,
        }
      : shipToStatePreviousComparison;
  const shipToZipComparison = compareAddressField(shipToZip, activePreviousShipTo?.zip, activePreviousShipTo, shipToComparisonLabel);
  const shipToCountryComparison = compareAddressField(shipToCountry, activePreviousShipTo?.country, activePreviousShipTo, shipToComparisonLabel);

  const shipByHelper = useMemo(() => shipByDateFieldHelper(shipByDate), [shipByDate]);

  const orderTotal = useMemo(
    () =>
      items.reduce((sum, item) => {
        if (item.waivedPreview) {
          return sum;
        }
        const quantity = Number.parseFloat(item.quantity) || 0;
        const unitPrice = Number.parseFloat(item.unitPrice) || 0;
        return sum + quantity * unitPrice;
      }, 0),
    [items],
  );

  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }),
    [],
  );
  const latestEndUserSkuHistory = useMemo(() => {
    const history = new Map<string, { unitPrice: number; salesOrderNumber: string; submitted: string | null }>();
    for (const item of endUserHistoryQuery.data?.items ?? []) {
      if (!history.has(item.sku)) {
        history.set(item.sku, {
          unitPrice: item.unitPrice,
          salesOrderNumber: item.salesOrderNumber,
          submitted: item.submitted,
        });
      }
    }
    return history;
  }, [endUserHistoryQuery.data?.items]);
  const endUserQuotedSkuLines = endUserQuotesQuery.data?.items ?? [];
  const magnetExemptions = endUserOption?.magnetExemptions ?? EMPTY_MAGNET_EXEMPTIONS;
  const magnetExemptionLabels = labelsForMagnetExemptions(magnetExemptions);
  const stockingOrder = isStockingReplenishmentOrder({
    clientStocking: clientOption?.stocking,
    clientFsAccountId: clientOption?.fsAccountId,
    endUserFsAccountId: endUserOption?.fsAccountId,
    clientNames: [clientOption?.label],
    endUserNames: [endUserOption?.label],
  });
  const rmaOrder = isRmaReplacementCustomerPurchaseOrder(customerPurchaseOrderNumber);
  const customerShippingFeeExempt = clientOption?.shippingFeeExempt === true;
  const userMerchandiseTotal = merchandiseTotal(
    items
      .filter((item) => (item.origin ?? "user") === "user")
      .map((item) => ({
        sku: item.sku,
        quantity: Number.parseFloat(item.quantity) || 0,
        unitPrice: Number.parseFloat(item.unitPrice) || 0,
      })),
    shippingSku,
  );
  const zeroMerchandiseShippingExempt =
    shippingChargeMode === "auto" &&
    userMerchandiseTotal <= 0 &&
    items.some((item) => (item.origin ?? "user") === "user" && item.sku.trim() !== "");
  const userChargeSignature = items
    .filter((item) => item.origin === "user")
    .map((item) => `${item.draftId}:${item.sku}:${item.quantity}:${item.unitPrice}`)
    .join("|");
  const magnetCatalog = useMemo(
    () =>
      Object.values(MAGNET_SKU_BY_FAMILY).map((sku) => {
        const catalog = skuCatalog.find((option) => option.sku.trim().toUpperCase() === sku);
        return {
          sku,
          description: formatMagnetCatalogDescription(catalog?.description, catalog?.variant) || sku,
          unitPrice: 0,
        };
      }),
    [skuCatalog],
  );
  const mergeAutomaticCharges = useCallback(
    (current: SalesOrderItemDraft[]): SalesOrderItemDraft[] => {
      const charged = applyAutomaticChargeLines({
        items: current.map((item) => ({
          sku: item.sku,
          quantity: Number.parseFloat(item.quantity) || 0,
          unitPrice: Number.parseFloat(item.unitPrice) || 0,
          productDescription: item.productDescription || null,
          remark: item.remark || null,
          origin: item.origin ?? "user",
        })),
        shippingSku,
        shippingQuantity: shippingSettings.quantity,
        shippingUnitPrice: shippingSettings.unitPrice,
        shippingDescription: "Shipping and Handling",
        magnetCatalog,
        exemptions: magnetExemptions,
        shippingMode: shippingChargeMode === "waive" ? "auto" : shippingChargeMode,
        magnetMode: magnetChargeMode === "waive" ? "auto" : magnetChargeMode,
        customerShippingFeeExempt,
        shipToCountry,
        shipToState,
        stockingOrder,
        rmaOrder,
        customerPurchaseOrderNumber,
      });
      const merged = keepShippingItemsLast(mergeChargedDrafts(current, charged, skuCatalog), shippingSku);
      return merged.map((item) => ({
        ...item,
        waivedPreview:
          (item.origin === "auto-magnet" && magnetChargeMode === "waive") ||
          (item.origin === "auto-shipping" && shippingChargeMode === "waive"),
      }));
    },
    [
      customerShippingFeeExempt,
      magnetCatalog,
      magnetChargeMode,
      magnetExemptions,
      rmaOrder,
      customerPurchaseOrderNumber,
      shipToCountry,
      shipToState,
      shippingChargeMode,
      shippingSettings.quantity,
      shippingSettings.unitPrice,
      shippingSku,
      skuCatalog,
      stockingOrder,
    ],
  );
  useEffect(() => {
    if (isMegaventoryControlled) {
      return;
    }
    setItems((current) => {
      const merged = mergeAutomaticCharges(current);
      return chargeDraftsMatch(current, merged) ? current : merged;
    });
  }, [isMegaventoryControlled, mergeAutomaticCharges, userChargeSignature]);
  const editorSnapshot = useMemo(
    () =>
      serializeSalesOrderEditorSnapshot({
        shipByDate,
        customerPurchaseOrderNumber,
        paymentTerms,
        clientFsAccountId: clientOption?.fsAccountId ?? null,
        endUserFsAccountId: endUserOption?.fsAccountId ?? null,
        shipToRecipient,
        shipToAttention,
        shipToAddressLine1,
        shipToAddressLine2,
        shipToCity,
        shipToState,
        shipToZip,
        shipToCountry,
        billToRecipient,
        billToLine1,
        billToLine2,
        billToLine3,
        billToCity,
        billToState,
        billToZip,
        billToCountry,
        pilotProgram,
        specialInstructions,
        shippingChargeMode,
        magnetChargeMode,
        trackingRecipientEmails,
        invoiceRecipientEmails,
        items: items.filter((item) => !item.waivedPreview).map((item) => ({
          sku: item.sku,
          productDescription: item.productDescription,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          remark: item.remark,
          origin: item.origin ?? "user",
        })),
        attachments: attachments.map((attachment) =>
          attachment.localFile != null
            ? `local:${attachment.fileName}:${attachment.localFile.size}`
            : `saved:${attachment.id}`,
        ),
      }),
    [
      attachments,
      billToCity,
      billToCountry,
      billToLine1,
      billToLine2,
      billToLine3,
      billToRecipient,
      billToState,
      billToZip,
      clientOption?.fsAccountId,
      customerPurchaseOrderNumber,
      endUserOption?.fsAccountId,
      items,
      magnetChargeMode,
      paymentTerms,
      pilotProgram,
      shipByDate,
      shipToAddressLine1,
      shipToAddressLine2,
      shipToAttention,
      shipToCity,
      shipToCountry,
      shipToRecipient,
      shipToState,
      shipToZip,
      shippingChargeMode,
      specialInstructions,
      trackingRecipientEmails,
      invoiceRecipientEmails,
    ],
  );
  useEffect(() => {
    if (!isEditMode || !shouldCaptureEditorBaseline || partyCatalogQuery.isLoading) {
      return;
    }
    const order = orderDetailQuery.data?.order;
    if (
      order != null &&
      !salesOrderEditorPartyBaselineReady(
        order.clientFsAccountId,
        order.endUserFsAccountId,
        clientOption?.fsAccountId,
        endUserOption?.fsAccountId,
      )
    ) {
      return;
    }
    if (autoDescriptionRequestsRef.current.size > 0) {
      return;
    }
    if (!isMegaventoryControlled && !chargeDraftsMatch(items, mergeAutomaticCharges(items))) {
      return;
    }
    setEditorBaseline(editorSnapshot);
    setShouldCaptureEditorBaseline(false);
  }, [
    clientOption?.fsAccountId,
    editorSnapshot,
    endUserOption?.fsAccountId,
    isEditMode,
    isMegaventoryControlled,
    items,
    mergeAutomaticCharges,
    orderDetailQuery.data?.order,
    partyCatalogQuery.isLoading,
    shouldCaptureEditorBaseline,
  ]);
  const isEditorDirty = isEditMode && editorBaseline != null && editorSnapshot !== editorBaseline;
  const editorDirtyHint = useMemo(
    () =>
      isEditorDirty
        ? formatSalesOrderEditorDirtyHint(listSalesOrderEditorDirtyFields(editorBaseline, editorSnapshot))
        : null,
    [editorBaseline, editorSnapshot, isEditorDirty],
  );
  useBrowserUnsavedChangesGuard(isEditorDirty);
  const salesOrderPageTitle =
    isEditMode
      ? number.trim() === ""
        ? "Sales Order"
        : `Sales Order ${number.trim()}`
      : "Create sales order";
  const highlightedHistoryKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const item of items) {
      const sku = item.sku.trim();
      if (sku === "") continue;
      const skuHistory = latestEndUserSkuHistory.get(sku);
      if (!skuHistory) continue;
      const enteredUnitPrice = item.unitPrice.trim() === "" ? null : Number.parseFloat(item.unitPrice);
      if (enteredUnitPrice == null || !Number.isFinite(enteredUnitPrice)) continue;
      if (Math.abs(enteredUnitPrice - skuHistory.unitPrice) >= 0.005) {
        keys.add(`${skuHistory.salesOrderNumber}__${sku}__${skuHistory.submitted ?? ""}`);
      }
    }
    return keys;
  }, [items, latestEndUserSkuHistory]);
  const orderRep = orderDetailQuery.data?.order;
  const {
    repFieldsLockedIn,
    salesManagerDisplay,
    repAgencyDisplay,
    repStaffDisplay,
    repFieldTitle,
  } = buildSalesOrderEditorRepDisplays({
    savedOrder: orderRep,
    draftEndUser: endUserOption,
    fallbackStatus: status,
  });
  const repStaffWarning = isPendingOrder ? (endUserOption?.repStaffWarning ?? null) : null;
  const approveValidationMessages = useMemo(
    () =>
      listSalesOrderApproveValidationMessages({
        isPendingOrder,
        customerPurchaseOrderNumber,
        shipByDate,
        clientOption,
        endUserOption,
        items,
        shipToCountry,
        shipToState,
        shipToAddressLine1,
        shipToCity,
        shipToZip,
      }),
    [
      clientOption,
      customerPurchaseOrderNumber,
      endUserOption,
      isPendingOrder,
      items,
      shipByDate,
      shipToAddressLine1,
      shipToCity,
      shipToCountry,
      shipToState,
      shipToZip,
    ],
  );
  const approveValidationMessage = formatSalesOrderApproveValidationMessage(approveValidationMessages);
  const hasProductionBatchItems = orderDetailQuery.data?.order.hasProductionBatchItems === true;
  const statusChangeBlockedHint = useMemo(
    () => resolveSalesOrderStatusChangeBlockedHint({ unsavedChangesHint: editorDirtyHint }),
    [editorDirtyHint],
  );
  const revertToPendingBlockedHint = useMemo(
    () =>
      resolveSalesOrderRevertToPendingBlockedHint({
        unsavedChangesHint: editorDirtyHint,
        hasProductionBatchItems,
      }),
    [editorDirtyHint, hasProductionBatchItems],
  );
  const approveBlockedHint = resolveSalesOrderApproveBlockedHint({
    isApproving: statusMutation.isPending,
    canEditOrder,
    editLockHolderDisplayName: editLock?.holder?.displayName,
    validationMessages: approveValidationMessages,
    unsavedChangesHint: statusChangeBlockedHint,
  });

  function isExpiredQuote(validThrough: string | null): boolean {
    return !quoteLineStillValid(validThrough);
  }

  async function populateSkuDescription(
    draftId: string,
    sku: string,
    options?: { fallbackDescription?: string; baselineRequestKey?: string },
  ) {
    if (!sku) {
      updateItemById(draftId, { productDescription: "", productDescriptionError: "" });
      return;
    }
    try {
      const response = await apiFetch<SalesOrderSkuDescriptionResponse>("/api/sales-orders/sku-description", {
        query: { sku },
      });
      const generated = response.description?.trim() ?? "";
      const fallback = (options?.fallbackDescription ?? "").trim();
      updateItemById(draftId, {
        productDescription: generated || fallback,
        productDescriptionError: generated || fallback ? "" : (response.descriptionError ?? ""),
      });
    } catch (error) {
      const fallback = (options?.fallbackDescription ?? "").trim();
      updateItemById(draftId, {
        productDescription: fallback,
        productDescriptionError: fallback ? "" : error instanceof Error ? error.message : "Could not generate description.",
      });
    } finally {
      if (options?.baselineRequestKey != null) {
        autoDescriptionRequestsRef.current.delete(options.baselineRequestKey);
        if (autoDescriptionRequestsRef.current.size === 0) {
          setShouldCaptureEditorBaseline(true);
        }
      }
    }
  }

  function handleSkuSelect(index: number, option: SalesOrderSkuOption) {
    const draftId = items[index]?.draftId;
    if (!draftId) return;
    updateItem(index, {
      sku: option.sku,
      templateId: option.templateId,
      productDescription: option.description,
      productDescriptionError: "",
      megaventoryId: option.megaventoryId,
      origin: "user",
    });
    if (option.sku) {
      void populateSkuDescription(draftId, option.sku, { fallbackDescription: option.description });
    }
  }

  async function downloadPoDocument(poDocument: PoViewerDocument) {
    try {
      if (poDocument.textBody != null) {
        const blob = new Blob([poDocument.textBody], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = window.document.createElement("a");
        link.href = url;
        link.download = poDocument.fileName;
        window.document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        return;
      }
      if (poDocument.orderAttachment) {
        await downloadAttachment(poDocument.orderAttachment);
        return;
      }
      if (!poDocument.downloadUrl) {
        return;
      }
      const result = await apiFetchBlob(poDocument.downloadUrl);
      const url = URL.createObjectURL(result.blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = result.originalFilename || poDocument.fileName;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Could not download attachment.");
    }
  }

  async function downloadAttachment(attachment: SalesOrderAttachment) {
    try {
      if (attachment.localUrl) {
        const link = window.document.createElement("a");
        link.href = attachment.localUrl;
        link.download = attachment.fileName;
        window.document.body.appendChild(link);
        link.click();
        link.remove();
        return;
      }
      const result = await apiFetchBlob(attachment.downloadUrl);
      const url = URL.createObjectURL(result.blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = result.originalFilename || attachment.fileName;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Could not download attachment.");
    }
  }

  function deleteAttachment(attachmentId: string) {
    setAttachments((current) => {
      const removed = current.find((attachment) => attachment.id === attachmentId);
      if (removed?.localUrl) {
        URL.revokeObjectURL(removed.localUrl);
      }
      const next = current.filter((attachment) => attachment.id !== attachmentId);
      setSelectedAttachmentId((selectedId) => {
        if (selectedId !== attachmentId) {
          return selectedId;
        }
        return next[0]?.id ?? null;
      });
      if (poAnalysis && removed?.fileName === poAnalysis.fileName) {
        setPoAnalysis(null);
      }
      return next;
    });
  }

  useEffect(() => {
    function clampToViewport() {
      setPreviewSidebarWidthPx((current) =>
        clampSalesOrderPreviewSidebarWidthPx(current, window.innerWidth),
      );
    }
    window.addEventListener("resize", clampToViewport);
    return () => window.removeEventListener("resize", clampToViewport);
  }, []);

  const onPreviewSidebarResizePointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    const handle = event.currentTarget;
    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startWidth = previewSidebarWidthPxRef.current;
    handle.setPointerCapture(pointerId);
    setIsPreviewSidebarResizing(true);
    window.document.body.style.cursor = "col-resize";
    window.document.body.style.userSelect = "none";

    function finishResize() {
      handle.removeEventListener("pointermove", onPointerMove);
      handle.removeEventListener("pointerup", onPointerUp);
      handle.removeEventListener("pointercancel", onPointerUp);
      if (handle.hasPointerCapture(pointerId)) {
        handle.releasePointerCapture(pointerId);
      }
      window.document.body.style.cursor = "";
      window.document.body.style.userSelect = "";
      setIsPreviewSidebarResizing(false);
      writeSalesOrderPreviewSidebarWidthCookie(previewSidebarWidthPxRef.current, window.document);
    }

    function onPointerMove(moveEvent: PointerEvent) {
      const nextWidth = clampSalesOrderPreviewSidebarWidthPx(
        startWidth + (startX - moveEvent.clientX),
        window.innerWidth,
      );
      previewSidebarWidthPxRef.current = nextWidth;
      setPreviewSidebarWidthPx(nextWidth);
    }

    function onPointerUp() {
      finishResize();
    }

    handle.addEventListener("pointermove", onPointerMove);
    handle.addEventListener("pointerup", onPointerUp);
    handle.addEventListener("pointercancel", onPointerUp);
  }, []);

  return (
    <HubWorkspacePage className="gap-0">
      <CreateSalesOrderSaveToast visible={isSaveConfirmationVisible} />
      <div className="flex shrink-0 flex-col gap-4 border-b border-slate-200 bg-slate-50 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          {isEditMode ? (
            <PageTitleWithHelp
              breadcrumbLabel={salesOrderPageTitle}
              hoverHint="Save this Pending order at any time. Customer PO, ship-by date, client, end user, complete ship-to address, SKU, and unit price are required only when you Approve. Hover the disabled Approve button to see what is still missing."
            >
              {salesOrderPageTitle}
            </PageTitleWithHelp>
          ) : (
            <PageTitleWithHelp
              breadcrumbLabel={salesOrderPageTitle}
              hoverHint="Create a sales order header and line items in the hub. The order is saved as Pending; submitted date is set automatically when you create it."
            >
              {salesOrderPageTitle}
            </PageTitleWithHelp>
          )}
          {approvalRequestId ? (
            <div className="mt-3 rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-800">
              This order is linked to approval request {approvalRequestQuery.data?.request.sourceTicketNumber || approvalRequestId}.
              {approvalRequestQuery.data?.aiStatus === "ready" ? " The ready AI draft was used to prefill available fields." : ""}
            </div>
          ) : null}
          {!hubWritesAllowed ? (
            <p className="mt-2 text-sm text-slate-500">Inside Sales write access is required to create or edit sales orders.</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2 overflow-visible py-0.5">
          {isEditMode ? (
            <SalesOrderEditControlGlyph
              isPendingOrder={isPendingOrder}
              isApproving={statusMutation.isPending && isPendingOrder}
              canEditOrder={canEditOrder}
              editLock={editLock}
              isChecking={editLockQuery.isLoading || acquireEditLockMutation.isPending}
              moduleWritesAllowed={hubWritesAllowed}
              onTakeOver={() => setIsTakeoverModalOpen(true)}
            />
          ) : null}
          {isEditMode && resolvedSalesOrderId != null ? (
            <SalesOrderDownloadMenu
              orderId={resolvedSalesOrderId}
              orderNumber={number || orderDetailQuery.data?.order.number || "Sales order"}
              invoices={orderDetailQuery.data?.invoices ?? []}
              canDownloadSalesOrderPdf={canDownloadSalesOrderPdf}
              density="header"
              onError={setSubmitError}
            />
          ) : null}
          {isEditMode && megaventoryOrderUrl ? (
            <a
              href={megaventoryOrderUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-10 items-center justify-center gap-1.5 rounded-md border border-slate-300 bg-white px-4 text-sm font-medium text-slate-900 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
            >
              Megaventory
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          ) : null}
          {hubWritesAllowed && isEditMode ? (
            <HubSaveButton
              type="button"
              variant={isEditorDirty ? "primary" : "secondary"}
              dirty={isEditorDirty}
              dirtyHint={editorDirtyHint}
              disabled={!canEditOrder}
              blockedHint={
                isPendingOrder
                  ? "You do not have editing access on this order."
                  : "Document is read-only while approved"
              }
              isLoading={saveMutation.isPending}
              onClick={submitOrder}
            >
              Save Changes
            </HubSaveButton>
          ) : hubWritesAllowed ? (
            <Button type="button" isLoading={saveMutation.isPending} onClick={submitOrder}>
              Create order
            </Button>
          ) : null}
          {hubWritesAllowed && isEditMode && isPendingOrder ? (
            <ReadOnlyHoverLayer
              active={approveBlockedHint != null}
              hoverHint={approveBlockedHint ?? ""}
              layout="inline"
            >
              <Button
                type="button"
                variant="secondary"
                isLoading={statusMutation.isPending}
                onClick={approveOrder}
                disabled={
                  !canEditOrder ||
                  approveValidationMessages.length > 0 ||
                  statusChangeBlockedHint != null
                }
              >
                Approve
              </Button>
            </ReadOnlyHoverLayer>
          ) : null}
          {hubWritesAllowed && isEditMode && isApprovedOrder ? (
            <ReadOnlyHoverLayer
              active={revertToPendingBlockedHint != null}
              hoverHint={revertToPendingBlockedHint ?? ""}
              layout="inline"
            >
              <Button
                type="button"
                variant="secondary"
                isLoading={statusMutation.isPending}
                onClick={revertToPending}
                disabled={revertToPendingBlockedHint != null}
              >
                Revert to Pending
              </Button>
            </ReadOnlyHoverLayer>
          ) : null}
          {isEditMode && orderId ? (
            <SalesOrderDetailOverflowMenu
              orderId={orderId}
              orderNumber={number || orderId}
              mvId={orderDetailQuery.data?.order.mvId ?? null}
              isPendingOrder={isPendingOrder}
              isAdmin={isIntegratorAdminUser}
            />
          ) : null}
        </div>
      </div>
      <div className="flex min-h-0 flex-1 items-stretch gap-4 overflow-hidden">
      <div className="min-w-0 flex-1 space-y-6 overflow-y-auto pr-1 pt-4">
      {hubWritesAllowed && isEditMode && isPendingOrder && (editLockQuery.isError || acquireEditLockMutation.isError || heartbeatEditLockMutation.isError) ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>
            {editLockQuery.error instanceof Error
              ? editLockQuery.error.message
              : acquireEditLockMutation.error instanceof Error
                ? acquireEditLockMutation.error.message
                : heartbeatEditLockMutation.error instanceof Error
                  ? heartbeatEditLockMutation.error.message
                  : "Could not confirm editing access."}
          </span>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => void editLockQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}
      {analyzePoMutation.isPending ? (
        <div className="text-sm text-slate-500">
          Running AI analysis and address checks. This can take a little longer when we compare against recent order history.
        </div>
      ) : null}
      {preloadError ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {preloadError} Refresh the page to try loading the catalogs again.
        </div>
      ) : null}
      {isEditMode && orderDetailQuery.isLoading ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Loading sales order details...
        </div>
      ) : null}
      {isEditMode && orderDetailQuery.isError ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {orderDetailQuery.error instanceof Error ? orderDetailQuery.error.message : "Could not load sales order details."}
        </div>
      ) : null}
      {saveMutation.isError ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{saveMutation.error instanceof Error ? saveMutation.error.message : isEditMode ? "Could not save sales order changes." : "Could not create sales order."}</span>
          {isSalesOrderVersionConflict(saveMutation.error) ? (
            <Button type="button" size="sm" variant="secondary" onClick={() => void reloadLatestOrderAfterConflict()}>
              Reload latest order
            </Button>
          ) : isSalesOrderEditLockConflict(saveMutation.error) ? (
            <Button type="button" size="sm" variant="secondary" onClick={() => void editLockQuery.refetch()}>
              Refresh editing access
            </Button>
          ) : null}
        </div>
      ) : null}
      {statusMutation.isError ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{statusMutation.error instanceof Error ? statusMutation.error.message : "Could not update sales order status."}</span>
          {isSalesOrderVersionConflict(statusMutation.error) ? (
            <Button type="button" size="sm" variant="secondary" onClick={() => void reloadLatestOrderAfterConflict()}>
              Reload latest order
            </Button>
          ) : isSalesOrderEditLockConflict(statusMutation.error) ? (
            <Button type="button" size="sm" variant="secondary" onClick={() => void editLockQuery.refetch()}>
              Refresh editing access
            </Button>
          ) : null}
        </div>
      ) : null}
      {submitError ? <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{submitError}</div> : null}
      {isEditMode && isMegaventoryControlled ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          This order is linked to Megaventory. Addresses, line items, dates, and customer fields follow the Megaventory
          document.
          {megaventoryOrderUrl ? (
            <>
              {" "}
              <a
                href={megaventoryOrderUrl}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-amber-950 underline decoration-amber-400 underline-offset-2 hover:text-amber-900"
              >
                Open in Megaventory
              </a>
            </>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>Order Details</CardTitle>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <div className={cn("inline-flex items-center rounded-md border px-3 py-1.5", statusAppearance.container)}>
              <span className={cn("mr-2 h-2 w-2 rounded-full", statusAppearance.dot)} aria-hidden="true" />
              <span className={cn("font-semibold", statusAppearance.value)}>{salesOrderStatusLabel(status)}</span>
            </div>
            {isEditMode ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5">
                <span className="mr-2 text-xs font-medium uppercase tracking-normal text-slate-500">Created</span>
                <span className="font-semibold text-slate-900">{formatDateUs(submitted) || "-"}</span>
              </div>
            ) : null}
            {isEditMode && orderDetailQuery.isSuccess ? (
              approvalTicketId ? (
                <Link
                  to={buildInsideSalesApprovalPath(approvalTicketId)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-brand-200 bg-brand-50 px-3 py-1.5 text-sm font-semibold text-brand-800 transition-colors hover:bg-brand-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                >
                  View approval
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-auto rounded-md px-3 py-1.5"
                  onClick={() => {
                    setLinkApprovalError(null);
                    setIsLinkApprovalModalOpen(true);
                  }}
                >
                  Link approval
                </Button>
              )
            ) : null}
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 xl:items-start">
            <fieldset
              disabled={!canEditMvControlledFields}
              className="order-1 m-0 min-w-0 border-0 p-0 disabled:opacity-100 md:col-start-1 md:row-start-1 xl:col-start-1 xl:row-start-1"
            >
              <div className="flex h-8 items-center">
                <Label htmlFor="so-po">Customer PO</Label>
              </div>
              <Input
                id="so-po"
                value={customerPurchaseOrderNumber}
                onChange={(event) => setCustomerPurchaseOrderNumber(event.target.value)}
                onBlur={(event) => commitCustomerPurchaseOrderNumber(event.target.value)}
                className="mt-1"
                aria-describedby={customerPoDuplicates.length > 0 ? "so-po-duplicate-warning" : undefined}
              />
              {customerPoDuplicates.length > 0 ? (
                <p id="so-po-duplicate-warning" className="mt-1 text-xs text-amber-700">
                  This PO is already on{" "}
                  {customerPoDuplicates.map((order, index) => (
                    <span key={order.id}>
                      {index > 0 ? (index === customerPoDuplicates.length - 1 ? " and " : ", ") : null}
                      <Link
                        to={buildInsideSalesSalesOrderEditPath(order.id)}
                        className="font-medium text-amber-800 underline decoration-amber-400 underline-offset-2 hover:text-amber-950"
                      >
                        {order.number}
                      </Link>
                    </span>
                  ))}
                  .
                </p>
              ) : null}
            </fieldset>
            <fieldset
              disabled={!canEditMvControlledFields}
              className="order-2 m-0 min-w-0 border-0 p-0 disabled:opacity-100 md:col-start-2 md:row-start-1 xl:col-start-2 xl:row-start-1"
            >
              <div className="flex h-8 items-center">
                <Label htmlFor="so-payment-terms">Payment terms</Label>
              </div>
              <select
                id="so-payment-terms"
                value={paymentTerms}
                disabled={pilotProgram}
                onChange={(event) => setPaymentTerms(event.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100 disabled:text-slate-700"
              >
                {(paymentTermsQuery.data?.options ?? []).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {pilotProgram ? (
                <p className="mt-1 text-xs text-slate-500">Pilot Program orders use Net 90.</p>
              ) : null}
            </fieldset>
            <fieldset
              disabled={!canEditMvControlledFields}
              className="order-7 m-0 min-w-0 border-0 p-0 disabled:opacity-100 md:col-start-1 md:row-start-4 xl:col-start-3 xl:row-start-1"
            >
              <div className="flex h-8 items-center">
                <Label htmlFor="so-ship-by">Ship by date</Label>
              </div>
              <ShipByDatePicker
                id="so-ship-by"
                value={shipByDate}
                onChange={setShipByDate}
                showWeekShortcuts={canEditMvControlledFields}
                className="mt-1"
              />
              {shipByHelper ? <p className="mt-1 text-xs text-slate-500">{shipByHelper}</p> : null}
            </fieldset>
            <fieldset
              disabled={!canEditMvControlledFields}
              className="order-3 m-0 min-w-0 border-0 p-0 disabled:opacity-100 md:col-start-1 md:row-start-2 xl:col-start-1 xl:row-start-2"
            >
              <PartyLookupField
                id="so-client"
                label="Client"
                kind="client"
                value={clientOption}
                onSelect={handleClientSelect}
                catalog={clientCatalog}
                isLoading={partyCatalogQuery.isLoading}
              />
            </fieldset>
            <fieldset
              disabled={!canEditMvControlledFields}
              className="order-4 m-0 min-w-0 border-0 p-0 disabled:opacity-100 md:col-start-2 md:row-start-2 xl:col-start-2 xl:row-start-2"
            >
              <PartyLookupField
                id="so-end-user"
                label="End user"
                kind="end-user"
                value={endUserOption}
                onSelect={handleEndUserSelect}
                catalog={endUserCatalog}
                isLoading={partyCatalogQuery.isLoading}
              />
            </fieldset>
            <fieldset
              disabled={!canEditMvControlledFields}
              className="order-8 m-0 min-w-0 border-0 p-0 disabled:opacity-100 md:col-start-2 md:row-start-4 xl:col-start-3 xl:row-span-2 xl:row-start-2"
            >
              <div className="flex h-8 items-center">
                <Label htmlFor="so-special-instructions">Customer-facing note</Label>
              </div>
              <TextArea
                id="so-special-instructions"
                value={specialInstructions}
                onChange={(event) => setSpecialInstructions(event.target.value)}
                className="mt-1 min-h-[6rem] resize-y font-sans xl:min-h-[8rem]"
              />
            </fieldset>
            <div className="order-5 min-w-0 md:col-start-1 md:row-start-3 xl:col-start-1 xl:row-start-3">
              <TextChipInput
                id="so-tracking-recipients"
                label="Tracking recipients"
                chips={trackingRecipientEmails}
                onChipsChange={setTrackingRecipientEmails}
                placeholder="Type an email and press Enter"
                disabled={!canEditOrder}
                className="[&>label]:flex [&>label]:h-8 [&>label]:items-center"
              />
            </div>
            <div className="order-6 min-w-0 md:col-start-2 md:row-start-3 xl:col-start-2 xl:row-start-3">
              <TextChipInput
                id="so-invoice-recipients"
                label="Invoice recipients"
                chips={invoiceRecipientEmails}
                onChipsChange={setInvoiceRecipientEmails}
                placeholder="Type an email and press Enter"
                disabled={!canEditOrder}
                className="[&>label]:flex [&>label]:h-8 [&>label]:items-center"
              />
            </div>
          </div>

          <fieldset disabled={!canEditMvControlledFields} className="disabled:opacity-100">
          <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <input
              id="so-pilot-program"
              type="checkbox"
              checked={pilotProgram}
              onChange={(event) => {
                const checked = event.target.checked;
                if (checked) {
                  if (paymentTerms !== "Net90") {
                    paymentTermsBeforePilotRef.current = paymentTerms;
                  }
                  setPaymentTerms("Net90");
                  setPilotProgram(true);
                  return;
                }
                const restore = paymentTermsBeforePilotRef.current;
                paymentTermsBeforePilotRef.current = null;
                if (restore != null && restore !== "") {
                  setPaymentTerms(restore);
                }
                setPilotProgram(false);
              }}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            <Label htmlFor="so-pilot-program" className="m-0 text-sm font-medium text-slate-900">
              Pilot Program
            </Label>
          </div>
          </fieldset>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label htmlFor="so-sales-manager">SGS Sales Manager</Label>
              <Input
                id="so-sales-manager"
                value={salesManagerDisplay}
                readOnly
                disabled
                className="mt-1 bg-slate-50"
                title={repFieldTitle}
                placeholder={repFieldsLockedIn ? undefined : "Select an end user"}
              />
            </div>
            <div>
              <Label htmlFor="so-rep-agency">Rep Agency</Label>
              <Input
                id="so-rep-agency"
                value={repAgencyDisplay}
                readOnly
                disabled
                className="mt-1 bg-slate-50"
                title={repFieldTitle}
                placeholder={repFieldsLockedIn ? undefined : "Select an end user"}
              />
            </div>
            <div>
              <Label htmlFor="so-rep-staff">Rep Staff</Label>
              <Input
                id="so-rep-staff"
                value={repStaffDisplay}
                readOnly
                disabled
                className="mt-1 bg-slate-50"
                title={repFieldTitle}
                placeholder={repFieldsLockedIn ? undefined : "Select an end user"}
              />
            </div>
          </div>
          {repStaffWarning && isPendingOrder ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {repStaffWarning}
            </div>
          ) : null}

          {isEditMode && canUseInsideSalesAdminSettings && repFieldsLockedIn ? (
            <div className="space-y-2">
              <HoverHintTrigger
                trigger={
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    isLoading={repSyncMutation.isPending}
                    disabled={resolvedSalesOrderId == null}
                    onClick={() => {
                      if (resolvedSalesOrderId != null) {
                        repSyncMutation.mutate(resolvedSalesOrderId);
                      }
                    }}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Re-run rep check
                  </Button>
                }
              >
                <div className="space-y-2">
                  <HoverHintDefinitionGrid
                    rows={[
                      {
                        label: "Last rep check",
                        value: lockedRepSyncAt ? formatTimestamp(lockedRepSyncAt) : "Never",
                      },
                      {
                        label: "Freshdesk ticket",
                        value:
                          lockedRepFreshdeskTicketId != null && lockedRepFreshdeskTicketId > 0 ? (
                            <a
                              href={freshdeskTicketUrl(lockedRepFreshdeskTicketId)}
                              target="_blank"
                              rel="noreferrer"
                              className="text-brand-700 hover:text-brand-800"
                            >
                              #{lockedRepFreshdeskTicketId}
                            </a>
                          ) : (
                            "None"
                          ),
                      },
                    ]}
                  />
                  {(orderDetailQuery.data?.activity ?? []).filter(
                    (entry) => entry.action === "Ran initial rep check" || entry.action === "Re-ran rep check",
                  ).length > 0 ? (
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Lock-in history</div>
                      <ul className="mt-1 space-y-1">
                        {(orderDetailQuery.data?.activity ?? [])
                          .filter(
                            (entry) =>
                              entry.action === "Ran initial rep check" || entry.action === "Re-ran rep check",
                          )
                          .slice(0, 5)
                          .map((entry) => (
                            <li key={entry.id} className="text-xs text-slate-800">
                              {formatTimestamp(entry.timestamp)}
                              {": "}
                              {entry.action}
                            </li>
                          ))}
                      </ul>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-700">
                      First check ran on approval. Later presses are recorded in the activity log.
                    </p>
                  )}
                </div>
              </HoverHintTrigger>
              {repSyncError ? (
                <p className="text-sm text-red-700" role="alert">
                  {repSyncError}
                </p>
              ) : null}
            </div>
          ) : null}

        </CardBody>
      </Card>

        <fieldset disabled={!canEditMvControlledFields} className="space-y-6 disabled:opacity-100">
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className={cn(isPoViewerOpen ? "text-base" : undefined)}>Bill To</CardTitle>
              <div className={cn("flex items-center gap-2", isPoViewerOpen ? "gap-1.5" : undefined)}>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(isPoViewerOpen ? "h-7 px-2 text-xs" : undefined)}
                  title={previousBillToHover}
                  onClick={applyPreviousBillTo}
                  disabled={!activePreviousBillTo}
                >
                  Use Previous
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(isPoViewerOpen ? "h-7 px-2 text-xs" : undefined)}
                  onClick={clearBillTo}
                >
                  Clear
                </Button>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
            <div>
              <AddressFieldLabel
                htmlFor="bill-to-recipient"
                label="Recipient"
                comparison={billToRecipientComparison}
                onApply={() => setBillToRecipient(activePreviousBillTo?.recipient ?? "")}
              />
              <Input id="bill-to-recipient" value={billToRecipient} onChange={(event) => setBillToRecipient(event.target.value)} className="mt-1" />
            </div>
            <div>
              <AddressFieldLabel
                htmlFor="bill-to-line1"
                label="Address line 1"
                comparison={billToLine1Comparison}
                onApply={() => setBillToLine1(activePreviousBillTo?.line1 ?? "")}
              />
              <Input id="bill-to-line1" value={billToLine1} onChange={(event) => setBillToLine1(event.target.value)} className="mt-1" />
            </div>
            <div>
              <AddressFieldLabel
                htmlFor="bill-to-line2"
                label="Address line 2"
                comparison={billToLine2Comparison}
                onApply={() => setBillToLine2(activePreviousBillTo?.line2 ?? "")}
              />
              <Input id="bill-to-line2" value={billToLine2} onChange={(event) => setBillToLine2(event.target.value)} className="mt-1" />
            </div>
            <div>
              <AddressFieldLabel
                htmlFor="bill-to-line3"
                label="Address line 3"
                comparison={billToLine3Comparison}
                onApply={() => setBillToLine3(activePreviousBillTo?.line3 ?? "")}
              />
              <Input id="bill-to-line3" value={billToLine3} onChange={(event) => setBillToLine3(event.target.value)} className="mt-1" />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <AddressFieldLabel
                  htmlFor="bill-to-country"
                  label="Country"
                  comparison={billToCountryComparison}
                  onApply={() => {
                    const nextCountry = normalizeSalesOrderCountryCode(activePreviousBillTo?.country);
                    setBillToCountry(nextCountry);
                    setBillToState((current) => regionAfterSalesOrderCountryChange(nextCountry, current));
                  }}
                />
                <div className="mt-1">
                  <AddressCountryPicker
                    id="bill-to-country"
                    value={billToCountry}
                    onChange={(next) => {
                      setBillToCountry(next);
                      setBillToState((current) => regionAfterSalesOrderCountryChange(next, current));
                    }}
                    disabled={!canEditOrder}
                  />
                </div>
              </div>
              <div>
                <AddressFieldLabel
                  htmlFor="bill-to-city"
                  label="City"
                  comparison={billToCityComparison}
                  onApply={() => setBillToCity(activePreviousBillTo?.city ?? "")}
                />
                <Input id="bill-to-city" value={billToCity} onChange={(event) => setBillToCity(event.target.value)} className="mt-1" />
              </div>
              <div>
                <AddressFieldLabel
                  htmlFor="bill-to-state"
                  label={salesOrderRegionField(billToCountry).regionLabel}
                  comparison={billToStateComparison}
                  onApply={() => setBillToState(normalizeSalesOrderRegionInput(billToCountry, activePreviousBillTo?.state))}
                />
                <div className="mt-1">
                  <AddressRegionPicker
                    id="bill-to-state"
                    country={billToCountry}
                    value={billToState}
                    onChange={setBillToState}
                    disabled={!canEditOrder}
                  />
                </div>
              </div>
              <div>
                <AddressFieldLabel
                  htmlFor="bill-to-zip"
                  label={salesOrderRegionField(billToCountry).postalLabel}
                  comparison={billToZipComparison}
                  onApply={() => setBillToZip(activePreviousBillTo?.zip ?? "")}
                />
                <Input id="bill-to-zip" value={billToZip} onChange={(event) => setBillToZip(event.target.value)} className="mt-1" />
                {billToZipCityMessage ? (
                  <p className="mt-1 text-sm text-amber-800">{billToZipCityMessage}</p>
                ) : null}
              </div>
            </div>
          </CardBody>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className={cn(isPoViewerOpen ? "text-base" : undefined)}>Ship To</CardTitle>
              <div className={cn("flex items-center gap-2", isPoViewerOpen ? "gap-1.5" : undefined)}>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(isPoViewerOpen ? "h-7 px-2 text-xs" : undefined)}
                  onClick={applyShipToSgs}
                >
                  Ship to SGS
                </Button>
                <div title={previousShipToHover}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={cn("pointer-events-none", isPoViewerOpen ? "h-7 px-2 text-xs" : undefined)}
                    disabled
                  >
                    Use Previous
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(isPoViewerOpen ? "h-7 px-2 text-xs" : undefined)}
                  onClick={clearShipTo}
                >
                  Clear
                </Button>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
            <div>
              <AddressFieldLabel
                htmlFor="ship-to-recipient"
                label="Recipient/Organization"
                comparison={shipToRecipientComparison}
              />
              <Input id="ship-to-recipient" value={shipToRecipient} onChange={(event) => setShipToRecipient(event.target.value)} className="mt-1" />
            </div>
            <div>
              <AddressFieldLabel
                htmlFor="ship-to-attention"
                label="Attention"
                comparison={shipToAttentionComparison}
              />
              <Input id="ship-to-attention" value={shipToAttention} onChange={(event) => setShipToAttention(event.target.value)} className="mt-1" />
            </div>
            <div>
              <AddressFieldLabel
                htmlFor="ship-to-line1"
                label="Address line 1"
                comparison={shipToLine1Comparison}
              />
              <Input id="ship-to-line1" value={shipToAddressLine1} onChange={(event) => setShipToAddressLine1(event.target.value)} className="mt-1" />
            </div>
            <div>
              <AddressFieldLabel
                htmlFor="ship-to-line2"
                label="Address line 2"
                comparison={shipToLine2Comparison}
              />
              <Input id="ship-to-line2" value={shipToAddressLine2} onChange={(event) => setShipToAddressLine2(event.target.value)} className="mt-1" />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <AddressFieldLabel
                  htmlFor="ship-to-country"
                  label="Country"
                  comparison={shipToCountryComparison}
                />
                <div className="mt-1">
                  <AddressCountryPicker
                    id="ship-to-country"
                    value={shipToCountry}
                    onChange={(next) => {
                      setShipToCountry(next);
                      setShipToState((current) => regionAfterSalesOrderCountryChange(next, current));
                    }}
                    disabled={!canEditOrder}
                  />
                </div>
              </div>
              <div>
                <AddressFieldLabel
                  htmlFor="ship-to-city"
                  label="City"
                  comparison={shipToCityComparison}
                />
                <Input id="ship-to-city" value={shipToCity} onChange={(event) => setShipToCity(event.target.value)} className="mt-1" />
              </div>
              <div>
                <AddressFieldLabel
                  htmlFor="ship-to-state"
                  label={salesOrderRegionField(shipToCountry).regionLabel}
                  comparison={shipToStateComparison}
                />
                <div className="mt-1">
                  <AddressRegionPicker
                    id="ship-to-state"
                    country={shipToCountry}
                    value={shipToState}
                    onChange={setShipToState}
                    disabled={!canEditOrder}
                  />
                </div>
              </div>
              <div>
                <AddressFieldLabel
                  htmlFor="ship-to-zip"
                  label={salesOrderRegionField(shipToCountry).postalLabel}
                  comparison={shipToZipComparison}
                />
                <Input id="ship-to-zip" value={shipToZip} onChange={(event) => setShipToZip(event.target.value)} className="mt-1" />
                {shipToZipCityMessage ? (
                  <p className="mt-1 text-sm text-amber-800">{shipToZipCityMessage}</p>
                ) : null}
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <CardTitle>Line Items</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              {shippingChargeMode === "waive" ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!canEditOrder}
                  onClick={() => setShippingChargeMode("auto")}
                >
                  Restore shipping charge
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowRemarksColumn((current) => !current)}
              >
                <MessageSquarePlus className="h-4 w-4" aria-hidden="true" />
                {showRemarksColumn ? "Hide remark" : "Add remark"}
              </Button>
              <Button type="button" variant="secondary" size="sm" disabled={!canEditOrder} onClick={addItem}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add item
              </Button>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            {stockingOrder || rmaOrder || magnetChargeMode === "waive" || magnetExemptionLabels.length > 0 || zeroMerchandiseShippingExempt ? (
              <div className="space-y-2">
                {stockingOrder ? (
                  <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span>
                      {normalizeStockingValue(clientOption?.stocking) === "mixed"
                        ? "This billed customer is mixed stocking, and the end user is that customer or one of its locations, so reset magnets are not added automatically."
                        : clientOption?.stocking?.trim()
                          ? `This billed customer is ${clientOption.stocking.trim()} stocking, so reset magnets are not added automatically.`
                          : "This is a stocking order, so reset magnets are not added automatically."}
                    </span>
                  </div>
                ) : null}
                {magnetChargeMode === "waive" ? (
                  <div className="flex items-start justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <span>
                      Automatic reset magnets are waived, so they are not on this order, the PDF, or downstream batches and invoices.
                    </span>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={!canEditOrder}
                      onClick={() => setMagnetChargeMode("auto")}
                    >
                      Restore reset magnets
                    </Button>
                  </div>
                ) : null}
                {rmaOrder ? (
                  <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span>
                      This order is for an RMA, so reset magnets are not added automatically.
                    </span>
                  </div>
                ) : null}
                {zeroMerchandiseShippingExempt ? (
                  <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span>
                      Merchandise total is $0, so shipping is not added automatically.
                    </span>
                  </div>
                ) : null}
                {magnetExemptionLabels.length > 0 ? (
                  <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span>
                      This customer is exempt from automatic {magnetExemptionLabels.join(" and ")} reset
                      magnets.
                    </span>
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className="min-h-[20rem] overflow-visible rounded-md border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-normal text-slate-500">
                  <tr>
                    <th className="px-3 py-2">SKU</th>
                    <th className="px-3 py-2">Product Description</th>
                    <th className="px-3 py-2 text-right">Quantity</th>
                    <th className="px-3 py-2 text-right">Unit Price (USD)</th>
                    <th className="px-3 py-2 text-right">Total Value (USD)</th>
                    {showRemarksColumn ? <th className="px-3 py-2">Remark</th> : null}
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                {items.map((item, index) => {
                  const lineTotal = (Number.parseFloat(item.quantity) || 0) * (Number.parseFloat(item.unitPrice) || 0);
                  const skuHistory = item.sku ? latestEndUserSkuHistory.get(item.sku) ?? null : null;
                  const skuQuote = item.sku
                    ? selectSalesOrderQuotedSkuLine(
                        activeSalesOrderQuoteLines(endUserQuotedSkuLines),
                        item.sku,
                        Number.parseFloat(item.quantity) || 0,
                      )
                    : null;
                  const enteredUnitPrice = item.unitPrice.trim() === "" ? null : Number.parseFloat(item.unitPrice);
                  const showNeverBoughtWarning =
                    endUserOption != null &&
                    !endUserHistoryQuery.isLoading &&
                    item.sku.trim() !== "" &&
                    skuHistory == null &&
                    !isSystemChargeItem(item);
                  const showPriceWarning =
                    skuHistory != null &&
                    enteredUnitPrice != null &&
                    Number.isFinite(enteredUnitPrice) &&
                    Math.abs(enteredUnitPrice - skuHistory.unitPrice) >= 0.005;
                  const showQuotedPriceWarning =
                    skuQuote != null &&
                    enteredUnitPrice != null &&
                    Number.isFinite(enteredUnitPrice) &&
                    Math.abs(enteredUnitPrice - skuQuote.unitPrice) >= 0.005;
                  const showExpiredQuoteWarning =
                    skuQuote != null &&
                    item.sku.trim() !== "" &&
                    isExpiredQuote(skuQuote.validThrough);
                  const showMissingSkuWarning = item.sku.trim() === "";
                  const hasSku = item.sku.trim() !== "";
                  const showMissingDescriptionWarning = hasSku && item.productDescription.trim() === "";
                  const showMissingUnitPriceWarning = hasSku && item.unitPrice.trim() === "";
                  const shippingItem = isShippingItem(item, shippingSku);
                  const systemChargeItem = isSystemChargeItem(item);
                  const autoMagnetItem = item.origin === "auto-magnet";
                  const waivedCharge = item.waivedPreview === true;
                  const showInvalidQuantityWarning =
                    !autoMagnetItem && isInvalidLineItemQuantity(item.quantity);
                  return (
                    <tr
                      key={item.draftId}
                      className={cn(waivedCharge && "bg-amber-50/70", draggedItemId === item.draftId && "opacity-50")}
                      onDragOver={(event) => {
                        if (!canEditOrder || !draggedItemId) return;
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        if (canEditOrder && draggedItemId) {
                          moveItem(draggedItemId, item.draftId);
                        }
                        setDraggedItemId(null);
                      }}
                    >
                      <td className="px-3 py-2 align-top w-[22%]">
                        <SkuLookupField
                          id={`item-sku-${index}`}
                          value={item}
                          onSelectOption={(option) => handleSkuSelect(index, option)}
                          catalog={skuCatalog}
                          isLoading={skuCatalogQuery.isLoading}
                          disabled={!canEditOrder || systemChargeItem}
                        />
                        {systemChargeItem ? (
                          <p className="mt-1 text-xs text-slate-500">
                            {waivedCharge
                              ? "Waived. Shown here only. Left off the order, the PDF, and downstream batches and invoices."
                              : autoMagnetItem
                                ? "Added automatically for FI reset magnets."
                                : "Added automatically for shipping."}
                          </p>
                        ) : null}
                        {showMissingSkuWarning ? (
                          <p className="mt-1 text-xs text-red-600">SKU is required.</p>
                        ) : null}
                        {showNeverBoughtWarning ? (
                          <p className="mt-1 text-xs text-red-600">This customer has never bought this part number.</p>
                        ) : null}
                        {showExpiredQuoteWarning ? (
                          <p className="mt-1 text-xs text-amber-700">
                            Latest quote {skuQuote?.quoteNumber ? `#${skuQuote.quoteNumber} ` : ""}expired on {formatDateUs(skuQuote?.validThrough) || "an earlier date"}.
                          </p>
                        ) : null}
                      </td>
                        <td className="px-3 py-2 align-top w-[30%]">
                          <Label htmlFor={`item-desc-${index}`} className="sr-only">
                            Product Description
                          </Label>
                          <TextArea
                            id={`item-desc-${index}`}
                            title={item.productDescription || "Product description"}
                            rows={2}
                            value={item.productDescription}
                            disabled={!canEditOrder || systemChargeItem}
                            onChange={(event) =>
                              updateItem(index, { productDescription: event.target.value, productDescriptionError: "" })
                            }
                            className="min-h-[2.5rem] resize-y font-sans"
                          />
                          {item.productDescriptionError ? (
                            <p className="mt-1 text-xs text-amber-700">{item.productDescriptionError}</p>
                          ) : showMissingDescriptionWarning ? (
                            <p className="mt-1 text-xs text-amber-700">Product description is empty.</p>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 align-top w-[10%]">
                          <Label htmlFor={`item-qty-${index}`} className="sr-only">
                            Quantity
                          </Label>
                          <Input
                            id={`item-qty-${index}`}
                            type="number"
                            min="0"
                            step="1"
                            value={item.quantity}
                            disabled={!canEditOrder || systemChargeItem}
                            aria-invalid={showInvalidQuantityWarning}
                            onChange={(event) => updateItem(index, { quantity: event.target.value })}
                            className={cn(
                              lineItemNumericInputClass,
                              "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
                              showInvalidQuantityWarning &&
                                "border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500",
                            )}
                          />
                          {showInvalidQuantityWarning ? (
                            <p className="mt-1 text-xs text-red-600">Quantity must be at least 1.</p>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 align-top w-[14%]">
                          <Label htmlFor={`item-price-${index}`} className="sr-only">
                            Unit Price (USD)
                          </Label>
                        <Input
                          id={`item-price-${index}`}
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.unitPrice}
                          disabled={!canEditOrder || systemChargeItem}
                          onChange={(event) => updateItem(index, { unitPrice: event.target.value })}
                          onBlur={() => {
                            const formatted = formatLineItemUnitPriceField(item.unitPrice);
                            if (formatted !== item.unitPrice) {
                              updateItem(index, { unitPrice: formatted });
                            }
                          }}
                          className={cn(
                            lineItemNumericInputClass,
                            "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
                          )}
                        />
                        {showMissingUnitPriceWarning ? (
                          <p className="mt-1 text-xs text-red-600">Unit price is required. Enter 0 if this line has no charge.</p>
                        ) : null}
                        {showPriceWarning ? (
                          <p className="mt-1 text-xs text-red-600">
                            Last sold price: {currencyFormatter.format(skuHistory.unitPrice)}
                          </p>
                        ) : null}
                        {showQuotedPriceWarning ? (
                          <p className="mt-1 text-xs text-amber-700">
                            Last quoted price: {currencyFormatter.format(skuQuote.unitPrice)} at qty {skuQuote.quantity}
                            {skuQuote.validThrough ? ` (quote ${skuQuote.quoteNumber}, expires ${formatDateUs(skuQuote.validThrough)})` : skuQuote.quoteNumber ? ` (quote ${skuQuote.quoteNumber})` : ""}
                          </p>
                        ) : null}
                      </td>
                        <td className="px-3 py-2 align-top w-[14%]">
                          <Label htmlFor={`item-total-${index}`} className="sr-only">
                            Total Value (USD)
                          </Label>
                          <Input id={`item-total-${index}`} value={lineTotal.toFixed(2)} disabled className={cn("bg-slate-50", lineItemNumericInputClass)} />
                        </td>
                        {showRemarksColumn ? (
                          <td className="px-3 py-2 align-top w-[22%]">
                            <Label htmlFor={`item-remark-${index}`} className="sr-only">
                              Remark
                            </Label>
                              <Input
                                id={`item-remark-${index}`}
                                title={item.remark || "Remark"}
                                value={item.remark}
                                disabled={!canEditOrder}
                                onChange={(event) => updateItem(index, { remark: event.target.value, showRemark: true })}
                                className={lineItemExpandableInputClass}
                              />
                          </td>
                        ) : null}
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          <div className="flex items-center justify-end">
                            {systemChargeItem ? (
                              <div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  disabled={!canEditOrder}
                                  aria-pressed={waivedCharge}
                                  aria-label={
                                    waivedCharge
                                      ? autoMagnetItem
                                        ? "Restore automatic reset magnets"
                                        : "Restore automatic shipping"
                                      : autoMagnetItem
                                        ? "Waive automatic reset magnets"
                                        : "Waive automatic shipping"
                                  }
                                  onClick={() => {
                                    if (autoMagnetItem) {
                                      setMagnetChargeMode(waivedCharge ? "auto" : "waive");
                                      return;
                                    }
                                    setShippingChargeMode(waivedCharge ? "auto" : "waive");
                                  }}
                                >
                                  {waivedCharge ? "Waived" : "Waive"}
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  type="button"
                                  draggable={canEditOrder}
                                  disabled={!canEditOrder}
                                  aria-label={`Move line ${index + 1}`}
                                  title="Drag to reorder this line"
                                  className={cn(
                                    "inline-flex h-8 w-8 items-center justify-center rounded text-slate-400",
                                    canEditOrder
                                      ? "cursor-grab hover:bg-slate-100 hover:text-slate-700 active:cursor-grabbing"
                                      : "cursor-not-allowed opacity-50",
                                  )}
                                  onDragStart={(event) => {
                                    event.dataTransfer.effectAllowed = "move";
                                    event.dataTransfer.setData("text/plain", item.draftId);
                                    setDraggedItemId(item.draftId);
                                  }}
                                  onDragEnd={() => setDraggedItemId(null)}
                                >
                                  <GripVertical className="h-4 w-4" aria-hidden="true" />
                                </button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeItem(index)}
                                  disabled={items.length === 1 && !shippingItem}
                                >
                                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end border-t border-slate-200 pt-3">
              <div className="w-full max-w-xs rounded-md bg-slate-50 px-4 py-3 text-right">
                <div className="text-xs font-medium uppercase tracking-normal text-slate-500">Order total</div>
                <div className="mt-1 text-lg font-semibold text-slate-950">
                  {currencyFormatter.format(orderTotal)}
                </div>
              </div>
            </div>

          </CardBody>
        </Card>
        <Card>
          <CardBody className="space-y-3 pt-6">
                <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-1">
                  <div
                    role="tab"
                    tabIndex={0}
                    aria-selected={historyTab === "orders"}
                    className={cn(
                      "cursor-pointer rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                      historyTab === "orders" ? "bg-white text-slate-950 shadow-sm" : "text-slate-600 hover:text-slate-900",
                    )}
                    onClick={() => setHistoryTab("orders")}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setHistoryTab("orders");
                      }
                    }}
                  >
                    End User Order History
                  </div>
                  <div
                    role="tab"
                    tabIndex={0}
                    aria-selected={historyTab === "quotes"}
                    className={cn(
                      "cursor-pointer rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                      historyTab === "quotes" ? "bg-white text-slate-950 shadow-sm" : "text-slate-600 hover:text-slate-900",
                    )}
                    onClick={() => setHistoryTab("quotes")}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setHistoryTab("quotes");
                      }
                    }}
                  >
                    Quote
                  </div>
                  {isEditMode ? (
                    <div
                      role="tab"
                      tabIndex={0}
                      aria-selected={historyTab === "activity"}
                      className={cn(
                        "cursor-pointer rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                        historyTab === "activity" ? "bg-white text-slate-950 shadow-sm" : "text-slate-600 hover:text-slate-900",
                      )}
                      onClick={() => setHistoryTab("activity")}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setHistoryTab("activity");
                        }
                      }}
                    >
                      Activity Log
                    </div>
                  ) : null}
                </div>
                {historyTab === "activity" ? (
                  <div className="space-y-3">
                    {isEditMode && resolvedSalesOrderId != null ? (
                      <div className="rounded-md border border-slate-200 bg-white p-3">
                        <Label htmlFor="so-activity-note">Add a note</Label>
                        <MentionTextarea
                          id="so-activity-note"
                          value={activityNoteDraft}
                          onChange={setActivityNoteDraft}
                          placeholder="Leave a note for others working on this order. Type @ to mention someone."
                          className="mt-1 min-h-[4.5rem] w-full rounded-md border border-slate-300 px-3 py-2 font-sans text-sm"
                          disabled={activityNoteMutation.isPending}
                        />
                        <div className="mt-2 flex items-center justify-end gap-3">
                          {activityNoteError ? (
                            <p className="flex-1 text-sm text-red-600">{activityNoteError}</p>
                          ) : null}
                          <Button
                            type="button"
                            variant="secondary"
                            disabled={activityNoteDraft.trim() === "" || activityNoteMutation.isPending}
                            isLoading={activityNoteMutation.isPending}
                            onClick={() => activityNoteMutation.mutate(activityNoteDraft.trim())}
                          >
                            Add note
                          </Button>
                        </div>
                      </div>
                    ) : null}
                    {(orderDetailQuery.data?.activity.length ?? 0) === 0 ? (
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                        No changes have been logged yet.
                      </div>
                    ) : (
                      <div className="max-h-[620px] space-y-3 overflow-auto pr-1">
                        {(orderDetailQuery.data?.activity ?? []).map((entry) => (
                          <div key={entry.id} className="rounded-md border border-slate-200 px-3 py-2">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-sm font-medium text-slate-900">{entry.action}</div>
                              <div className="shrink-0 text-xs text-slate-500">
                                {new Date(entry.timestamp).toLocaleString()}
                                {entry.actorDisplayName ? ` • ${entry.actorDisplayName}` : ""}
                              </div>
                            </div>
                            <div className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-600">{entry.detail}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : !endUserOption ? (
                  <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                    This panel will populate after you choose an end user.
                  </div>
                ) : historyTab === "orders" && endUserHistoryQuery.isLoading ? (
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                    Loading recent order history...
                  </div>
                ) : historyTab === "orders" && endUserHistoryQuery.isError ? (
                  <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {endUserHistoryQuery.error?.message ?? "Could not load end user history."}
                  </div>
                ) : historyTab === "orders" && (endUserHistoryQuery.data?.items.length ?? 0) === 0 ? (
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                    No previous sales order items found for this end user.
                  </div>
                ) : historyTab === "orders" ? (
                  <div className="overflow-hidden rounded-md border border-slate-200">
                    <div className="max-h-[620px] overflow-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50 text-left text-xs font-semibold uppercase tracking-normal text-slate-500">
                          <tr>
                            <th className="px-3 py-2">Order</th>
                            <th className="px-3 py-2">Date</th>
                            <th className="px-3 py-2">SKU</th>
                            <th className="px-3 py-2 text-right">Qty</th>
                            <th className="px-3 py-2 text-right">Price</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {(endUserHistoryQuery.data?.items ?? []).map((item, index) => {
                            const historyKey = `${item.salesOrderNumber}__${item.sku}__${item.submitted ?? ""}`;
                            const isHighlighted = highlightedHistoryKeys.has(historyKey);
                            return (
                              <tr
                                key={`${item.salesOrderId}-${item.sku}-${index}`}
                                className={isHighlighted ? "bg-red-50/80" : undefined}
                              >
                                <td className="px-3 py-2 font-medium text-slate-900">{item.salesOrderNumber}</td>
                                <td className="px-3 py-2 text-slate-600">{formatDateUs(item.submitted) || "-"}</td>
                                <td className="px-3 py-2 text-slate-700">{item.sku}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-slate-700">{item.quantity}</td>
                                <td className={`px-3 py-2 text-right tabular-nums ${isHighlighted ? "font-semibold text-red-700" : "text-slate-900"}`}>
                                  {currencyFormatter.format(item.unitPrice)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : endUserQuotesQuery.isLoading ? (
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                    Loading recent quotes...
                  </div>
                ) : endUserQuotesQuery.isError ? (
                  <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {endUserQuotesQuery.error?.message ?? "Could not load end user quotes."}
                  </div>
                ) : (endUserQuotesQuery.data?.items.length ?? 0) === 0 ? (
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                    No previous quotes found for this end user.
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-md border border-slate-200">
                    <div className="max-h-[620px] overflow-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50 text-left text-xs font-semibold uppercase tracking-normal text-slate-500">
                          <tr>
                            <th className="px-3 py-2">Quote</th>
                            <th className="px-3 py-2">Expire date</th>
                            <th className="px-3 py-2">SKU</th>
                            <th className="px-3 py-2 text-right">Qty</th>
                            <th className="px-3 py-2 text-right">Price</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {(endUserQuotesQuery.data?.items ?? []).map((item, index) => (
                            <tr key={`${item.quoteId}-${item.sku}-${index}`}>
                              <td className="px-3 py-2 font-medium text-slate-900">{item.quoteNumber}</td>
                              <td className="px-3 py-2 text-slate-600">{formatDateUs(item.validThrough) || "-"}</td>
                              <td className="px-3 py-2 text-slate-700">{item.sku}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-slate-700">{item.quantity}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-slate-900">
                                {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(item.unitPrice)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
      </div>
      </fieldset>
      </div>
      </div>

      <aside
        className="hidden h-full shrink-0 xl:flex"
        style={{
          width: isPoViewerOpen ? previewSidebarWidthPx : SALES_ORDER_PREVIEW_SIDEBAR_COLLAPSED_PX,
        }}
      >
        {isPoViewerOpen ? (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize attachments preview"
            title="Drag to resize"
            tabIndex={0}
            className={cn(
              "w-1.5 shrink-0 cursor-col-resize touch-none self-stretch border-l border-slate-200 bg-slate-200/80 hover:bg-brand-300",
              isPreviewSidebarResizing && "bg-brand-400",
            )}
            onPointerDown={onPreviewSidebarResizePointerDown}
          />
        ) : null}
        <div
          ref={attachmentPaneRef}
          data-attachment-drop={attachmentDropActive ? "true" : undefined}
          className="group relative flex h-full min-w-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm data-[attachment-drop=true]:[&_iframe]:pointer-events-none"
          onDragEnter={onAttachmentPaneDragEnter}
          onDragOver={onAttachmentPaneDragOver}
          onDragLeave={onAttachmentPaneDragLeave}
          onDrop={onAttachmentPaneDrop}
        >
          <button
            type="button"
            onClick={() => setIsPoViewerOpen((current) => !current)}
            className="flex w-11 shrink-0 flex-col items-center justify-center gap-2 border-r border-slate-200 bg-slate-50 px-1.5 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
            title={isPoViewerOpen ? "Collapse attachments" : "Expand attachments"}
          >
            {isPoViewerOpen ? <ChevronRight className="h-4 w-4" aria-hidden="true" /> : <ChevronLeft className="h-4 w-4" aria-hidden="true" />}
            <span className="[writing-mode:vertical-rl] text-xs font-semibold uppercase tracking-normal">
              Attachments
            </span>
          </button>
          {isPoViewerOpen ? (
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="shrink-0 space-y-3 border-b border-slate-200 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-950">Attachments</div>
                    {!selectedPoDocument ? (
                      <div className="mt-1 text-xs text-slate-500">
                        Add a customer PO or switch between files, including the Freshdesk ticket.
                      </div>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <input
                      ref={attachmentInputRef}
                      id="sales-order-attachments"
                      type="file"
                      accept={SALES_ORDER_ATTACHMENT_ACCEPT}
                      multiple
                      className="sr-only"
                      disabled={!canEditOrder || isUploadingAttachments}
                      onChange={(event) => {
                        addLocalAttachments(Array.from(event.target.files ?? []));
                        event.target.value = "";
                      }}
                    />
                    <HoverHintTrigger
                      closeOnTriggerActivate
                      trigger={
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={!canEditOrder || isUploadingAttachments}
                          onClick={() => attachmentInputRef.current?.click()}
                        >
                          <Paperclip className="h-4 w-4" aria-hidden="true" />
                          {isUploadingAttachments ? "Uploading..." : "Attach"}
                        </Button>
                      }
                    >
                      Attach a customer PO PDF or image, or drop files on this panel. Freshdesk ticket text and files
                      appear in the list when this order is linked to an approval ticket.
                    </HoverHintTrigger>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={runPoAnalysis}
                      isLoading={analyzePoMutation.isPending}
                      disabled={
                        analyzePoMutation.isPending ||
                        !canEditMvControlledFields ||
                        !isAnalyzablePoDocument(selectedPoDocument)
                      }
                    >
                      {analyzePoMutation.isPending ? "Analyzing..." : "Analyze"}
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setIsAnalysisPreviewOpen(true)} disabled={!poAnalysis}>
                      Preview
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setIsAnalysisLogOpen(true)}>
                      Log
                    </Button>
                  </div>
                </div>
                {poViewerDocuments.length === 0 ? (
                  <p className="text-sm text-slate-500">No files yet.</p>
                ) : (
                  <ul className="max-h-36 space-y-1 overflow-y-auto">
                    {poViewerDocuments.map((poDocument) => {
                      const selected = selectedPoDocument?.id === poDocument.id;
                      const canRemove = canEditOrder && poDocument.kind === "order_attachment" && poDocument.orderAttachment != null;
                      const showFreshdeskLink =
                        poDocument.kind === "email_thread" && approvalFreshdeskTicketId != null;
                      return (
                        <li key={poDocument.id} className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setSelectedAttachmentId(poDocument.id)}
                            className={cn(
                              "min-w-0 flex-1 truncate rounded-md px-2 py-1 text-left text-sm",
                              selected ? "bg-brand-50 font-medium text-brand-800" : "text-slate-600 hover:bg-slate-50",
                            )}
                          >
                            {poDocument.kindLabel}: {poDocument.fileName}
                          </button>
                          {showFreshdeskLink ? (
                            <a
                              href={freshdeskTicketUrl(approvalFreshdeskTicketId)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-brand-700"
                              title={`Open Freshdesk ticket ${approvalFreshdeskTicketId}`}
                              aria-label={`Open Freshdesk ticket ${approvalFreshdeskTicketId}`}
                            >
                              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                            </a>
                          ) : null}
                          {canRemove ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              aria-label={`Remove ${poDocument.fileName}`}
                              onClick={() => deleteAttachment(poDocument.orderAttachment!.id)}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden />
                            </Button>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-slate-50">
                {selectedPoDocument?.textBody != null ? (
                  <div className="h-full overflow-auto bg-white p-4">
                    <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-slate-800">
                      {selectedPoDocument.textBody}
                    </pre>
                  </div>
                ) : selectedPoDocumentPreviewQuery.isLoading || approvalDocumentsQuery.isLoading ? (
                  <p className="py-12 text-center text-sm text-slate-500">Loading preview…</p>
                ) : selectedPoDocumentPreviewQuery.isError ? (
                  <p className="px-3 py-8 text-center text-sm text-red-700">
                    {selectedPoDocumentPreviewQuery.error instanceof Error
                      ? selectedPoDocumentPreviewQuery.error.message
                      : "Could not load attachment preview."}
                  </p>
                ) : selectedPoDocumentPreviewUrl && selectedPoDocument != null ? (
                  <div className="flex min-h-0 flex-1 flex-col px-2 pb-2 pt-0">
                    <HubBlobUrlPreview
                      previewUrl={selectedPoDocumentPreviewUrl}
                      filename={selectedPoDocument.fileName}
                      contentType={selectedPoDocument.mimeType}
                      fillAvailableHeight
                      hidePdfSidebar
                    />
                  </div>
                ) : selectedPoDocument ? (
                  <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center text-sm text-slate-500">
                    <div>No inline preview for this file type.</div>
                    <Button type="button" size="sm" variant="secondary" onClick={() => void downloadPoDocument(selectedPoDocument)}>
                      <Download className="h-4 w-4" aria-hidden="true" />
                      Download
                    </Button>
                  </div>
                ) : (
                  <p className="py-12 text-center text-sm text-slate-500">Select a file to preview it.</p>
                )}
              </div>
            </div>
          ) : null}
          <div
            className="pointer-events-none invisible absolute inset-0 z-20 flex items-center justify-center border-2 border-dashed border-brand-500 bg-brand-50/80 group-data-[attachment-drop=true]:visible"
            aria-hidden="true"
          >
            <span className="rounded-md bg-white px-3 py-2 text-sm font-medium text-brand-800 shadow-sm">
              Drop to attach
            </span>
          </div>
        </div>
      </aside>
      </div>

      {hubWritesAllowed && isTakeoverModalOpen ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setIsTakeoverModalOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-white shadow-xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Take over sales order editing"
          >
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-slate-950">Take Over Editing?</h2>
              <p className="mt-1 text-sm text-slate-500">
                {editLock?.holder?.displayName ?? "Another user"} currently controls this order.
              </p>
            </div>
            <div className="space-y-3 px-5 py-4 text-sm text-slate-700">
              <p>
                Their page will become read-only immediately. Any unsaved work on their screen will not be saved.
              </p>
              {takeoverEditLockMutation.isError ? (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-700">
                  {takeoverEditLockMutation.error instanceof Error
                    ? takeoverEditLockMutation.error.message
                    : "Could not take over editing."}
                </div>
              ) : null}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <Button type="button" variant="ghost" onClick={() => setIsTakeoverModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                isLoading={takeoverEditLockMutation.isPending}
                onClick={() => takeoverEditLockMutation.mutate()}
              >
                Take over editing
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {isLinkApprovalModalOpen ? (
        <SalesOrderLinkApprovalModal
          orderLabel={number || "this sales order"}
          isLinking={linkApprovalMutation.isPending}
          isCreating={createApprovalTicketMutation.isPending}
          linkError={linkApprovalError}
          onClose={() => {
            if (!linkApprovalMutation.isPending && !createApprovalTicketMutation.isPending) {
              setLinkApprovalError(null);
              setIsLinkApprovalModalOpen(false);
            }
          }}
          onLink={(reference) => linkApprovalMutation.mutate(reference)}
          onCreateNew={() => createApprovalTicketMutation.mutate()}
        />
      ) : null}

      {isAnalysisPreviewOpen ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setIsAnalysisPreviewOpen(false)}
        >
          <div
            className="max-h-[85vh] w-full max-w-3xl overflow-auto rounded-lg bg-white shadow-xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="PO analysis preview"
          >
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-950">PO Analysis Preview</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    {poAnalysis?.fileName ?? selectedPoDocument?.fileName ?? "No attachment selected"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={applyPoAnalysis}
                    disabled={!canEditMvControlledFields}
                  >
                    Apply analysis
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setIsAnalysisPreviewOpen(false)}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
            <div className="space-y-5 px-5 py-4">
              {poAnalysis ? (
                <>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Warnings</div>
                    <ul className="mt-2 space-y-1 text-sm text-slate-600">
                      {poAnalysis.warnings.map((warning, index) => (
                        <li key={index}>- {warning}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                      <div className="text-xs font-semibold uppercase tracking-normal text-slate-500">Customer PO</div>
                      <div className="mt-1 text-sm text-slate-800">{poAnalysis.extracted.customerPo ?? "None yet"}</div>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                      <div className="text-xs font-semibold uppercase tracking-normal text-slate-500">Client</div>
                      <div className="mt-1 text-sm text-slate-800">
                        {poAnalysis.extracted.clientName ?? "None yet"}
                        {poAnalysis.extracted.clientFsAccountId ? ` (FS ${poAnalysis.extracted.clientFsAccountId})` : ""}
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                      <div className="text-xs font-semibold uppercase tracking-normal text-slate-500">End User</div>
                      <div className="mt-1 text-sm text-slate-800">
                        {poAnalysis.extracted.endUserName ?? "None yet"}
                        {poAnalysis.extracted.endUserFsAccountId ? ` (FS ${poAnalysis.extracted.endUserFsAccountId})` : ""}
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                      <div className="text-xs font-semibold uppercase tracking-normal text-slate-500">Line Items</div>
                      <div className="mt-1 text-sm text-slate-800">{poAnalysis.extracted.items.length} extracted</div>
                    </div>
                  </div>
                  <div className="rounded-md border border-slate-200 p-3">
                    <div className="text-xs font-semibold uppercase tracking-normal text-slate-500">Customer-facing note</div>
                    <div className="mt-1 whitespace-pre-wrap text-sm text-slate-800">
                      {poAnalysis.extracted.specialInstructions ?? "None yet"}
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="rounded-md border border-slate-200 p-3">
                      <div className="text-xs font-semibold uppercase tracking-normal text-slate-500">Bill To</div>
                      <div className="mt-2 grid gap-3 md:grid-cols-2">
                        <div>
                          <div className="mb-1 text-[11px] font-semibold uppercase tracking-normal text-slate-500">AI</div>
                          <div className="space-y-1 text-sm text-slate-800">
                            {formatAddressPreview(poAnalysis.extracted.billTo).length > 0 ? (
                              formatAddressPreview(poAnalysis.extracted.billTo).map((line, index) => (
                                <div key={`bill-to-ai-${index}`}>{line}</div>
                              ))
                            ) : (
                              <div className="text-slate-500">No Bill To address extracted yet.</div>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <div className="text-[11px] font-semibold uppercase tracking-normal text-slate-500">Last Client Bill To</div>
                            {poAnalysis.historicalContext.clientBillTo ? (
                              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${addressesMatch(poAnalysis.extracted.billTo, poAnalysis.historicalContext.clientBillTo) ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                                {addressesMatch(poAnalysis.extracted.billTo, poAnalysis.historicalContext.clientBillTo) ? "Matches" : "Differs"}
                              </span>
                            ) : null}
                          </div>
                          <div className="space-y-1 text-sm text-slate-800">
                            {poAnalysis.historicalContext.clientBillTo && formatAddressPreview(poAnalysis.historicalContext.clientBillTo).length > 0 ? (
                              formatAddressPreview(poAnalysis.historicalContext.clientBillTo).map((line, index) => (
                                <div key={`bill-to-history-${index}`}>{line}</div>
                              ))
                            ) : (
                              <div className="text-slate-500">No recent client Bill To found.</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-200 p-3">
                      <div className="text-xs font-semibold uppercase tracking-normal text-slate-500">Ship To</div>
                      <div className="mt-2 grid gap-3 md:grid-cols-2">
                        <div>
                          <div className="mb-1 text-[11px] font-semibold uppercase tracking-normal text-slate-500">AI</div>
                          <div className="space-y-1 text-sm text-slate-800">
                            {formatAddressPreview(poAnalysis.extracted.shipTo).length > 0 ? (
                              formatAddressPreview(poAnalysis.extracted.shipTo).map((line, index) => (
                                <div key={`ship-to-ai-${index}`}>{line}</div>
                              ))
                            ) : (
                              <div className="text-slate-500">No Ship To address extracted yet.</div>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <div className="text-[11px] font-semibold uppercase tracking-normal text-slate-500">Last End User Ship To</div>
                            {poAnalysis.historicalContext.endUserShipTo ? (
                              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${addressesMatch(poAnalysis.extracted.shipTo, poAnalysis.historicalContext.endUserShipTo) ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                                {addressesMatch(poAnalysis.extracted.shipTo, poAnalysis.historicalContext.endUserShipTo) ? "Matches" : "Differs"}
                              </span>
                            ) : null}
                          </div>
                          <div className="space-y-1 text-sm text-slate-800">
                            {poAnalysis.historicalContext.endUserShipTo && formatAddressPreview(poAnalysis.historicalContext.endUserShipTo).length > 0 ? (
                              formatAddressPreview(poAnalysis.historicalContext.endUserShipTo).map((line, index) => (
                                <div key={`ship-to-history-${index}`}>{line}</div>
                              ))
                            ) : (
                              <div className="text-slate-500">No recent end user Ship To found.</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-md border border-slate-200 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xs font-semibold uppercase tracking-normal text-slate-500">Extracted Line Items</div>
                      <div className="text-xs text-slate-500">{poAnalysis.extracted.items.length} extracted</div>
                    </div>
                    {poAnalysis.extracted.items.length > 0 ? (
                      <div className="mt-3 overflow-hidden rounded-md border border-slate-200">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-normal text-slate-500">
                            <tr>
                              <th className="px-3 py-2">Raw SKU</th>
                              <th className="px-3 py-2">Matched SKU</th>
                              <th className="px-3 py-2 text-right">Qty</th>
                              <th className="px-3 py-2 text-right">Price</th>
                              <th className="px-3 py-2 text-right">Confidence</th>
                              <th className="px-3 py-2">Warning</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 bg-white">
                            {poAnalysis.extracted.items.map((item, index) => (
                              <tr key={`analysis-item-${index}`}>
                                <td className="px-3 py-2 font-medium text-slate-900">{item.rawSku ?? "-"}</td>
                                <td className="px-3 py-2 text-slate-700">{item.matchedSku ?? "-"}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                                  {item.quantity != null ? item.quantity : "-"}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                                  {item.unitPrice != null ? currencyFormatter.format(item.unitPrice) : "-"}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                                  {item.confidence != null ? `${Math.round(item.confidence * 100)}%` : "-"}
                                </td>
                                <td className="px-3 py-2 text-slate-700">{item.warning ?? "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="mt-2 text-sm text-slate-500">No line items extracted yet.</div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-sm text-slate-500">Run analysis to generate a preview.</div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {isAnalysisLogOpen ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setIsAnalysisLogOpen(false)}
        >
          <div
            className="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-lg bg-white shadow-xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="PO analysis log"
          >
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-950">PO Analysis Log</h2>
                  <p className="mt-1 text-sm text-slate-500">Recent analysis events and errors.</p>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => setIsAnalysisLogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
            <div className="px-5 py-4">
              {analysisLog.length === 0 ? (
                <div className="text-sm text-slate-500">No log entries yet.</div>
              ) : (
                <div className="space-y-3">
                  {analysisLog.map((entry) => (
                    <div
                      key={entry.id}
                      className={cn(
                        "rounded-md border px-3 py-3 text-sm",
                        entry.level === "error"
                          ? "border-red-200 bg-red-50 text-red-700"
                          : "border-slate-200 bg-slate-50 text-slate-700",
                      )}
                    >
                      <div className="mb-1 text-xs font-semibold uppercase tracking-normal">{entry.timestamp}</div>
                      <div>{entry.message}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </HubWorkspacePage>
  );
}

